import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { DataContext } from './DataContextValue';
import { apiFetch } from '../utils/apiUtils';
import * as api from '../services/api.axios';
import apiService from '../services/apiService';
import { API_BASE_URL, API_ORIGIN } from '../services/axios';
import { useAuth } from '../hooks/useAuth';
import { authService } from '../services/authService';
import socketService from '../services/socketService';

// Backend API base for absolute proxy URLs (avoid relative paths on static hosting)
const API_BASE = API_BASE_URL;
const API_INFO_URL = `${API_ORIGIN}/api`;

// Module-scoped cache for lightweight backend feature probes and admin status
// so we don't refetch `/api` and `/auth/profile` on every refresh or render.
const backendFeatures = {
  referrals: undefined,
  settingsKeyStore: undefined,
};

let cachedAdminStatus = {
  checkedAt: 0,
  ok: false,
  tokenPresent: false,
  isAdmin: false,
};
const ADMIN_STATUS_TTL_MS = 60 * 1000; // 60 seconds

// Note: useData hook is exported from hooks/useData.js to avoid circular dependencies

export const DataProvider = ({ children }) => {
  // Investment Plans - fetched from API
  const [investmentPlans, setInvestmentPlans] = useState([]);
  const [loadingInvestmentPlans, setLoadingInvestmentPlans] = useState(false);

  const [userInvestments, setUserInvestments] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);

  // All investments (admin only) - ALL investments from ALL users
  const [allInvestments, setAllInvestments] = useState([]);
  const [loadingAllInvestments, setLoadingAllInvestments] = useState(false);

  const [walletBalance, setWalletBalance] = useState(0);
  const [goldRate, setGoldRate] = useState(0);
  const [loadingGoldRate, setLoadingGoldRate] = useState(false);

  // Users state for synchronization between admin and user panels
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // User Bank Details - now with state management
  const [userBankDetails, setUserBankDetails] = useState([]);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // KYC Records state
  const [kycRecords, setKycRecords] = useState([]);
  const [loadingKycRecords, setLoadingKycRecords] = useState(false);
  const kycRetryRef = useRef({ attempts: 0, timer: null, delay: 1000 });

  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Referral data state
  const [referralCode, setReferralCode] = useState('');
  const [referralStats, setReferralStats] = useState({});
  const [referralHistory, setReferralHistory] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  // Wallet transactions state
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loadingWalletTransactions, setLoadingWalletTransactions] = useState(false);

  // Settlement configuration and records
  const [settlementConfig, setSettlementConfig] = useState({
    settlementDate: 'last',      // 'last' day of month or specific date (1-31)
    autoSettle: false,           // Auto-settle on date
    requireAdminApproval: true,  // Manual settlement trigger
    lastSettlementDate: null,
    nextSettlementDate: null
  });
  const [settlements, setSettlements] = useState([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  // ROI Schedule Settings for automatic processing
  const [roiScheduleSettings, setROIScheduleSettings] = useState({
    autoProcessEnabled: false,
    processingTime: '09:00', // HH:MM format
    lastProcessedDate: null,
    lastProcessedTime: null,
    nextScheduledRun: null
  });

  // Get auth context
  const { user, userType } = useAuth();

  // Backend connectivity status
  const [backendStatus, setBackendStatus] = useState({
    state: 'connecting',
    message: 'Connecting to server... ',
    lastChecked: null,
    attempts: 0,
    error: null,
    nextRetryAt: null,
  });
  const monitorRef = useRef({ timer: null, delay: 5000 });

  const runHealthCheck = async (manual = false) => {
    setBackendStatus((prev) => ({
      ...prev,
      state: 'connecting',
      message: 'Connecting to server... ',
      lastChecked: new Date().toISOString(),
    }));
    try {
      await apiService.health.server();
      await apiService.health.database();
      setBackendStatus({
        state: 'online',
        message: 'Backend is ready',
        lastChecked: new Date().toISOString(),
        attempts: 0,
        error: null,
        nextRetryAt: null,
      });
    } catch (err) {
      const now = Date.now();
      const nextDelay = Math.min((monitorRef.current?.delay || 5000) * 2, 60000);
      const scheduleDelay = manual ? monitorRef.current?.delay || 5000 : monitorRef.current?.delay || 5000;
      const nextRetryAt = new Date(now + scheduleDelay).toISOString();
      setBackendStatus((prev) => ({
        state: 'offline',
        message: prev.attempts >= 3 ? 'Backend unavailable. Possible maintenance.' : 'Backend unavailable. Retrying...',
        lastChecked: new Date().toISOString(),
        attempts: (prev.attempts || 0) + 1,
        error: err?.message || 'Connection failed',
        nextRetryAt,
      }));
      try {
        if (monitorRef.current?.timer) clearTimeout(monitorRef.current.timer);
      } catch { }
      monitorRef.current.delay = nextDelay;
      monitorRef.current.timer = setTimeout(() => runHealthCheck(false), scheduleDelay);
    }
  };

  const retryBackendCheck = () => {
    try {
      if (monitorRef.current?.timer) clearTimeout(monitorRef.current.timer);
    } catch { }
    monitorRef.current.delay = 5000;
    runHealthCheck(true);
  };

  useEffect(() => {
    // Renamed from runHealthCheck to doMonitor for clarity if needed, or keep runHealthCheck
    // Assuming doMonitor is intended to be runHealthCheck(false)
    runHealthCheck(false);
    return () => {
      if (monitorRef.current.timer) clearTimeout(monitorRef.current.timer);
    };
  }, [user]); // Added user as dependency

  // Load ROI schedule settings from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('roi_schedule_settings');
      if (saved) {
        setROIScheduleSettings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load ROI schedule settings:', error);
    }
  }, []);

  // Save ROI schedule settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('roi_schedule_settings', JSON.stringify(roiScheduleSettings));
    } catch (error) {
      console.error('Failed to save ROI schedule settings:', error);
    }
  }, [roiScheduleSettings]);

  // Automatic ROI Processing Scheduler
  useEffect(() => {
    if (!roiScheduleSettings.autoProcessEnabled) return;

    const checkSchedule = () => {
      const now = new Date();
      const [hours, minutes] = roiScheduleSettings.processingTime.split(':');
      const today = now.toISOString().split('T')[0];
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      // Check if it's the right time and hasn't been processed today
      if (currentHour === parseInt(hours) &&
        currentMinute === parseInt(minutes) &&
        roiScheduleSettings.lastProcessedDate !== today) {

        console.log('Auto-processing ROI at scheduled time:', roiScheduleSettings.processingTime);

        // Call the actual processDailyROI function (defined later in the component)
        // We'll handle this via a callback to avoid dependency issues
        setTimeout(() => {
          setROIScheduleSettings(prev => ({
            ...prev,
            lastProcessedDate: today,
            lastProcessedTime: now.toISOString()
          }));
        }, 100);
      }
    };

    // Check every minute
    const interval = setInterval(checkSchedule, 60000);

    // Also check immediately on mount
    checkSchedule();

    return () => clearInterval(interval);
  }, [roiScheduleSettings.autoProcessEnabled, roiScheduleSettings.processingTime, roiScheduleSettings.lastProcessedDate]);

  // Helper to ensure we have a usable access token before admin-protected mutations
  const ensureAdminAuth = async () => {
    // Try to ensure a fresh access token if possible
    let token = localStorage.getItem('auth_token');
    if (!token && localStorage.getItem('refresh_token')) {
      try {
        const res = await authService.refreshToken();
        if (res?.accessToken) token = res.accessToken;
      } catch { }
    }

    // If there is no token at all, we can short‑circuit without any network calls.
    if (!token) {
      cachedAdminStatus = {
        checkedAt: Date.now(),
        ok: false,
        tokenPresent: false,
        isAdmin: false,
      };
      return cachedAdminStatus;
    }

    // Check frontend admin flag FIRST (this overrides backend for known admin emails)
    const frontendUserType = localStorage.getItem('swarna_user_type');
    const isFrontendAdmin = frontendUserType === 'admin';

    // If frontend already marked this user as admin, trust it and avoid hitting /auth/profile.
    if (isFrontendAdmin) {
      cachedAdminStatus = {
        checkedAt: Date.now(),
        ok: true,
        tokenPresent: true,
        isAdmin: true,
      };
      return cachedAdminStatus;
    }

    // Otherwise, reuse a recently fetched admin status to avoid spamming /auth/profile.
    const now = Date.now();
    if (cachedAdminStatus.tokenPresent && now - cachedAdminStatus.checkedAt < ADMIN_STATUS_TTL_MS) {
      return cachedAdminStatus;
    }

    let isAdmin = false;
    try {
      const profile = await authService.getProfile();
      const role = (profile?.role || profile?.user?.role || '').toString().toLowerCase();
      isAdmin = role === 'admin';
    } catch {
      isAdmin = false;
    }

    cachedAdminStatus = {
      checkedAt: Date.now(),
      ok: !!token && isAdmin,
      tokenPresent: !!token,
      isAdmin,
    };
    return cachedAdminStatus;
  };

  // Helper to fetch plans with admin-aware endpoint
  const fetchPlansAdminAware = async () => {
    // Prefer admin endpoint when we're in admin context; gracefully fallback if unauthorized
    try {
      const adminStatus = await ensureAdminAuth();
      // Only hit admin endpoint if we BOTH have a token and are admin
      if (adminStatus?.ok) {
        try {
          const adminPlans = await api.planApi.getAllPlansAdmin();
          console.log('🔍 Admin plans raw response:', adminPlans);
          // Handle different response structures
          const unwrapped = Array.isArray(adminPlans) ? adminPlans :
            (Array.isArray(adminPlans?.data) ? adminPlans.data :
              (Array.isArray(adminPlans?.plans) ? adminPlans.plans : []));
          if (unwrapped.length > 0) return unwrapped;
        } catch (e) {
          // Expected 401/403 when not properly authenticated - silently fallback to public list
          if (e?.response?.status === 401 || e?.response?.status === 403) {
            // Suppress console errors for expected auth failures
          } else {
            console.warn('Admin plans endpoint error (falling back to public):', e.message);
          }
        }
      }
    } catch { }
    // Fallback to public endpoint
    const publicPlans = await api.planApi.getPlans();
    console.log('🔍 Public plans raw response:', publicPlans);
    // Handle different response structures
    const unwrapped = Array.isArray(publicPlans) ? publicPlans :
      (Array.isArray(publicPlans?.data) ? publicPlans.data :
        (Array.isArray(publicPlans?.plans) ? publicPlans.plans : []));
    return unwrapped;
  };

  // Load investment plans from API
  const loadInvestmentPlans = async () => {
    setLoadingInvestmentPlans(true);
    try {
      const plans = await fetchPlansAdminAware();
      console.log('📦 Raw plans from API:', plans);
      console.log('📦 First plan structure:', plans[0]);

      // Normalize backend plan shape to UI shape expected across components
      const normalized = plans.map(p => ({
        id: p._id || p.id || `plan-${Date.now()}`,
        title: p.name || p.title || '',
        name: p.name || p.title || '', // Add name property as well
        description: p.description || '',
        minAmount: typeof p.minInvestment === 'number' ? p.minInvestment : (p.minAmount ?? 0),
        maxAmount: typeof p.maxInvestment === 'number' ? p.maxInvestment : (p.maxAmount ?? 0),
        roiPercentMonthly: p.roiPercentMonthly || (typeof p.roiPercentage === 'number' ? p.roiPercentage : 0),
        roiPercentYearly: p.roiPercentYearly || (p.roiPercentMonthly * 12) || (typeof p.roiPercentage === 'number' ? p.roiPercentage * 12 : 0),
        roiPercentAnnual: p.roiPercentYearly || (p.roiPercentMonthly * 12) || (typeof p.roiPercentage === 'number' ? p.roiPercentage * 12 : 0),
        roiPercentDaily: p.roiPercentDaily || ((p.roiPercentYearly || 0) / 365),
        durationMonths: p.durationMonths ?? p.duration ?? 0,
        active: typeof p.isActive === 'boolean' ? p.isActive : (p.active ?? true),
        displayOrder: p.displayOrder ?? 0,
        color: p.color || 'bg-gradient-to-r from-blue-400 to-blue-600',
        tags: Array.isArray(p.tags) ? p.tags : [],
        features: Array.isArray(p.features) ? p.features : [],
        // UI-only defaults to avoid undefined access in admin forms
        eligibility: p.eligibility && typeof p.eligibility === 'object' ? {
          minKycLevel: p.eligibility.minKycLevel ?? 'basic',
          minWalletBalance: p.eligibility.minWalletBalance ?? 0,
          maxActiveInvestments: p.eligibility.maxActiveInvestments ?? 10,
        } : {
          minKycLevel: 'basic',
          minWalletBalance: 0,
          maxActiveInvestments: 10,
        }
      }));
      console.log('✅ Normalized plans:', normalized);
      console.log('✅ First normalized plan:', normalized[0]);
      setInvestmentPlans(normalized);
    } catch (error) {
      console.error('Error loading investment plans:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Do NOT clear existing plans; backoff and retry once using admin-aware fetch
        setTimeout(async () => {
          try {
            const plans = await fetchPlansAdminAware();
            const normalized = plans.map(p => ({
              id: p._id || p.id || `plan-${Date.now()}`,
              title: p.name || p.title || '',
              description: p.description || '',
              minAmount: typeof p.minInvestment === 'number' ? p.minInvestment : (p.minAmount ?? 0),
              maxAmount: typeof p.maxInvestment === 'number' ? p.maxInvestment : (p.maxAmount ?? 0),
              roiPercentMonthly: typeof p.roiPercentage === 'number' ? p.roiPercentage : (p.roiPercentMonthly ?? 0),
              roiPercentAnnual: typeof p.roiPercentage === 'number' ? p.roiPercentage * 12 : (p.roiPercentAnnual ?? 0),
              durationMonths: p.durationMonths ?? p.duration ?? 0,
              active: typeof p.isActive === 'boolean' ? p.isActive : (p.active ?? true),
              displayOrder: p.displayOrder ?? 0,
              color: p.color || 'bg-gradient-to-r from-blue-400 to-blue-600',
              tags: Array.isArray(p.tags) ? p.tags : [],
              features: Array.isArray(p.features) ? p.features : [],
              eligibility: p.eligibility && typeof p.eligibility === 'object' ? {
                minKycLevel: p.eligibility.minKycLevel ?? 'basic',
                minWalletBalance: p.eligibility.minWalletBalance ?? 0,
                maxActiveInvestments: p.eligibility.maxActiveInvestments ?? 10,
              } : {
                minKycLevel: 'basic',
                minWalletBalance: 0,
                maxActiveInvestments: 10,
              }
            }));
            setInvestmentPlans(normalized);
          } catch (retryError) {
            console.error('Retry failed for loading investment plans:', retryError);
            addNotification({
              title: 'Error Loading Investment Plans',
              message: 'Failed to load investment plans due to rate limiting. Please refresh the page.',
              type: 'error'
            });
          }
        }, 2500); // Wait ~2.5 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      // On other errors, do not nuke list; just notify
      addNotification({
        title: 'Error Loading Investment Plans',
        message: 'Failed to load investment plans. Please try again later.',
        type: 'error'
      });
    } finally {
      setLoadingInvestmentPlans(false);
    }
  };

  // Helper: normalize backend investment -> UI shape expected in admin components
  const normalizeInvestment = (inv) => {
    if (!inv) return inv;
    const u = inv.userId && typeof inv.userId === 'object' ? inv.userId : {};
    const toDate = (d) => {
      try { return d ? new Date(d).toISOString().split('T')[0] : ''; } catch { return ''; }
    };
    return {
      id: inv._id || inv.id || `inv-${Date.now()}`,
      userId: (inv.userId && inv.userId._id) ? inv.userId._id.toString() : (inv.userId || ''),
      investorName: u.name || inv.investorName || '',
      investorEmail: u.email || inv.investorEmail || '',
      planName: inv.planName || '',
      amount: Number(inv.amount ?? 0),
      startDate: toDate(inv.startDate || inv.createdAt),
      endDate: toDate(inv.endDate),
      status: inv.status || 'active',
      roiPaid: Number(inv.roiPaid ?? 0),
      nextPayout: '',
      monthlyROI: Number(inv.monthlyROI ?? 0),
      totalMonths: Number(inv.totalMonths ?? 0),
      completedMonths: Number(inv.completedMonths ?? 0)
    };
  };

  // Load user investments from API (scoped to current user)
  const loadUserInvestments = async () => {
    setLoadingInvestments(true);
    try {
      const uid = (user?.id || user?._id || '').toString();
      if (!uid) {
        setUserInvestments([]);
        return;
      }
      const resp = await api.investmentApi.getInvestmentsByUserId(uid);
      const raw = Array.isArray(resp) ? resp : (Array.isArray(resp?.investments) ? resp.investments : []);
      const investments = raw.map(normalizeInvestment);
      setUserInvestments(investments);
    } catch (error) {
      console.error('Error loading user investments:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const uid = (user?.id || user?._id || '').toString();
            if (!uid) { setUserInvestments([]); return; }
            const resp = await api.investmentApi.getInvestmentsByUserId(uid);
            const raw = Array.isArray(resp) ? resp : (Array.isArray(resp?.investments) ? resp.investments : []);
            const investments = raw.map(normalizeInvestment);
            setUserInvestments(investments);
          } catch (retryError) {
            console.error('Retry failed for loading user investments:', retryError);
            setUserInvestments([]);
            addNotification({
              title: 'Error Loading Investments',
              message: 'Failed to load investments due to rate limiting. Please refresh the page.',
              type: 'error',
              audience: 'system'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      setUserInvestments([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Investments',
        message: 'Failed to load your investments. Please try again later.',
        type: 'error',
        audience: 'system'
      });
    } finally {
      setLoadingInvestments(false);
    }
  };

  // Load users from API
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const resp = await api.userApi.getUsers();
      const rawList = Array.isArray(resp) ? resp : (Array.isArray(resp?.users) ? resp.users : []);
      const list = rawList.map((u) => ({
        ...u,
        id: u.id || u._id || u.userId || `user-${Date.now()}`,
        name: u.name || u.fullName || u.username || 'User',
        email: u.email || '',
        phone: u.phone || u.mobile || '',
        joinDate: u.joinDate || (u.createdAt ? new Date(u.createdAt).toISOString().split('T')[0] : (u.joinedAt ? new Date(u.joinedAt).toISOString().split('T')[0] : '')),
        kycStatus: u.kycStatus || (u.kyc && u.kyc.status) || 'pending',
        status: u.status || u.accountStatus || 'active',
        totalInvestment: Number(u.totalInvestment ?? u.investmentTotal ?? 0),
        currentROI: Number(u.currentROI ?? u.roi ?? 0),
        lastLogin: u.lastLogin || u.lastSeen || 'Never',
        referralCode: u.referralCode || u.refCode || '',
        referredUsers: Array.isArray(u.referredUsers) ? u.referredUsers : [],
        referralBonuses: Number(u.referralBonuses ?? 0),
        totalReferralEarnings: Number(u.totalReferralEarnings ?? 0),
        fraudAlerts: Array.isArray(u.fraudAlerts) ? u.fraudAlerts : []
      }));
      setUsers(list);
    } catch (error) {
      console.error('Error loading users:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const resp = await api.userApi.getUsers();
            const rawList = Array.isArray(resp) ? resp : (Array.isArray(resp?.users) ? resp.users : []);
            const list = rawList.map((u) => ({
              ...u,
              id: u.id || u._id || u.userId || `user-${Date.now()}`,
              name: u.name || u.fullName || u.username || 'User',
              email: u.email || '',
              phone: u.phone || u.mobile || '',
              joinDate: u.joinDate || (u.createdAt ? new Date(u.createdAt).toISOString().split('T')[0] : (u.joinedAt ? new Date(u.joinedAt).toISOString().split('T')[0] : '')),
              kycStatus: u.kycStatus || (u.kyc && u.kyc.status) || 'pending',
              status: u.status || u.accountStatus || 'active',
              totalInvestment: Number(u.totalInvestment ?? u.investmentTotal ?? 0),
              currentROI: Number(u.currentROI ?? u.roi ?? 0),
              lastLogin: u.lastLogin || u.lastSeen || 'Never',
              referralCode: u.referralCode || u.refCode || '',
              referredUsers: Array.isArray(u.referredUsers) ? u.referredUsers : [],
              referralBonuses: Number(u.referralBonuses ?? 0),
              totalReferralEarnings: Number(u.totalReferralEarnings ?? 0),
              fraudAlerts: Array.isArray(u.fraudAlerts) ? u.fraudAlerts : []
            }));
            setUsers(list);
          } catch (retryError) {
            console.error('Retry failed for loading users:', retryError);
            setUsers([]);
            addNotification({
              title: 'Error Loading Users',
              message: 'Failed to load users due to rate limiting. Please refresh the page.',
              type: 'error'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      setUsers([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Users',
        message: 'Failed to load user data. Please try again later.',
        type: 'error'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load bank details from API
  const loadBankDetails = async () => {
    setLoadingBankDetails(true);
    try {
      const bankData = await api.bankApi.getBankDetails();
      const list = Array.isArray(bankData)
        ? bankData
        : (Array.isArray(bankData?.bankDetails) ? bankData.bankDetails : []);
      const normalized = list.map((d) => ({
        ...d,
        id: d.id || d._id,
      }));
      setUserBankDetails(normalized);
    } catch (error) {
      console.error('Error loading bank details:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const bankData = await api.bankApi.getBankDetails();
            const list = Array.isArray(bankData)
              ? bankData
              : (Array.isArray(bankData?.bankDetails) ? bankData.bankDetails : []);
            const normalized = list.map((d) => ({
              ...d,
              id: d.id || d._id,
            }));
            setUserBankDetails(normalized);
          } catch (retryError) {
            console.error('Retry failed for loading bank details:', retryError);
            setUserBankDetails([]);
            addNotification({
              title: 'Error Loading Bank Details',
              message: 'Failed to load bank details due to rate limiting. Please refresh the page.',
              type: 'error'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      setUserBankDetails([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Bank Details',
        message: 'Failed to load bank account information. Please try again later.',
        type: 'error'
      });
    } finally {
      setLoadingBankDetails(false);
    }
  };

  // Load transactions from API
  const loadTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const transactionData = await api.transactionApi.getTransactions();
      const list = Array.isArray(transactionData)
        ? transactionData
        : (Array.isArray(transactionData?.transactions) ? transactionData.transactions : []);
      setTransactions(list);
    } catch (error) {
      console.error('Error loading transactions:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const transactionData = await api.transactionApi.getTransactions();
            setTransactions(transactionData);
          } catch (retryError) {
            console.error('Retry failed for loading transactions:', retryError);
            setTransactions([]);
            addNotification({
              title: 'Error Loading Transactions',
              message: 'Failed to load transactions due to rate limiting. Please refresh the page.',
              type: 'error'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      setTransactions([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Transactions',
        message: 'Failed to load transaction history. Please try again later.',
        type: 'error'
      });
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Load KYC records from API (admin => all, user => own)
  const loadKycRecords = async () => {
    setLoadingKycRecords(true);
    try {
      console.info('Loading KYC records');
      const uid = (user?.id || user?._id || '').toString();
      let rawList = [];

      // Decide endpoint based on admin capability
      let isAdmin = false;
      try {
        const adminStatus = await ensureAdminAuth();
        isAdmin = !!adminStatus?.ok;
      } catch { }

      if (isAdmin) {
        const resp = await api.kycApi.getKycRecords();
        rawList = Array.isArray(resp) ? resp : (Array.isArray(resp?.kycRecords) ? resp.kycRecords : []);
      } else if (uid) {
        const resp = await api.kycApi.getKycRecordByUserId(uid);
        // Normalize single-object or wrapped response to array
        rawList = Array.isArray(resp) ? resp : (resp ? [(resp.kyc || resp)] : []);
      } else {
        // Not authenticated; nothing to load
        setKycRecords([]);
        return;
      }

      const toStr = (v) => (v === null || v === undefined) ? '' : String(v);
      const fmtDate = (d) => {
        try { return new Date(d).toISOString().split('T')[0]; } catch { return ''; }
      };
      // Normalize various backend file shapes (string URL or object with url/path/filename/size)
      const fileMeta = (val) => {
        if (!val) return null;
        // Simple string URL
        if (typeof val === 'string') {
          const filename = val.split('/').pop() || 'document';
          return { url: val, filename, size: '', uploaded: true };
        }
        // Object from backend (e.g., { url, filename, size } or { path, originalname, size })
        if (typeof val === 'object') {
          const rawUrl = val.url || val.path || val.location || '';
          const filename = val.filename || val.originalname || (typeof rawUrl === 'string' && rawUrl.split('/').pop()) || 'document';
          const size = val.sizeLabel || (val.size != null ? String(val.size) : '');
          // If we at least have a filename, treat as uploaded
          return { url: rawUrl || '', filename, size, uploaded: true };
        }
        return null;
      };

      const normalized = rawList.map((r) => {
        const u = r?.userId && typeof r.userId === 'object' ? r.userId : {};
        const docs = r?.documents || {};
        const docObj = {
          aadhaar: fileMeta(docs.aadhaar),
          pan: fileMeta(docs.pan),
          bankStatement: fileMeta(docs.bankStatement),
          photo: docs.photo ? fileMeta(docs.photo) : null,
        };
        const uploadedCount = Object.values(docObj).filter(d => d && d.uploaded).length;
        const progressPercent = Math.round((uploadedCount / 4) * 100);
        return {
          id: r?._id || r?.id || `kyc-${Date.now()}`,
          userId: ((u?._id || r?.userId || '') + '').toString(),
          userName: toStr(u?.name || r?.userName || 'User'),
          email: toStr(u?.email || r?.email || ''),
          phone: toStr(u?.phone || r?.phone || ''),
          panNumber: toStr(r?.panNumber || ''),
          aadhaarNumber: toStr(r?.aadhaarNumber || ''),
          bankName: toStr(r?.bankName || ''),
          bankAccount: toStr(r?.bankAccount || ''),
          ifscCode: toStr(r?.ifscCode || ''),
          status: toStr(r?.status || u?.kycStatus || 'pending'),
          submittedDate: fmtDate(r?.submittedAt || r?.createdAt),
          approvedDate: r?.approvedAt ? fmtDate(r?.approvedAt) : undefined,
          rejectedDate: r?.rejectedAt ? fmtDate(r?.rejectedAt) : undefined,
          isVerified: (r?.status || u?.kycStatus) === 'approved',
          progressPercent,
          documents: docObj,
          adminNotes: r?.adminNotes || ''
        };
      });

      setKycRecords(normalized);
      try { if (kycRetryRef.current?.timer) clearTimeout(kycRetryRef.current.timer); } catch { }
      kycRetryRef.current = { attempts: 0, timer: null, delay: 1000 };
    } catch (error) {
      // 404 for user-scoped KYC means the user simply hasn't submitted KYC yet.
      // Treat as an empty state (no notification), otherwise new accounts see a scary error on first login.
      if (error?.status === 404 && userType !== 'admin') {
        setKycRecords([]);
        return;
      }

      // If attempted admin endpoint failed with 401/403, gracefully degrade to user-scope
      const uid = (user?.id || user?._id || '').toString();
      if ((error?.status === 401 || error?.status === 403 || /access\s*denied|forbidden/i.test(error?.message || '')) && uid) {
        try {
          const resp = await api.kycApi.getKycRecordByUserId(uid);
          const rawList = Array.isArray(resp) ? resp : (resp ? [(resp.kyc || resp)] : []);
          // Reuse normalization logic (inline for brevity)
          const toStr = (v) => (v === null || v === undefined) ? '' : String(v);
          const fmtDate = (d) => { try { return new Date(d).toISOString().split('T')[0]; } catch { return ''; } };
          // Same file normalizer as main KYC loader
          const fileMeta = (val) => {
            if (!val) return null;
            if (typeof val === 'string') {
              const filename = val.split('/').pop() || 'document';
              return { url: val, filename, size: '', uploaded: true };
            }
            if (typeof val === 'object') {
              const rawUrl = val.url || val.path || val.location || '';
              const filename = val.filename || val.originalname || (typeof rawUrl === 'string' && rawUrl.split('/').pop()) || 'document';
              const size = val.sizeLabel || (val.size != null ? String(val.size) : '');
              return { url: rawUrl || '', filename, size, uploaded: true };
            }
            return null;
          };
          const normalized = rawList.map((r) => {
            const u = r?.userId && typeof r.userId === 'object' ? r.userId : {};
            const docs = r?.documents || {};
            const docObj = { aadhaar: fileMeta(docs.aadhaar), pan: fileMeta(docs.pan), bankStatement: fileMeta(docs.bankStatement), photo: docs.photo ? fileMeta(docs.photo) : null };
            const uploadedCount = Object.values(docObj).filter(d => d && d.uploaded).length;
            const progressPercent = Math.round((uploadedCount / 4) * 100);
            return {
              id: r?._id || r?.id || `kyc-${Date.now()}`,
              userId: ((u?._id || r?.userId || '') + '').toString(),
              userName: toStr(u?.name || r?.userName || 'User'),
              email: toStr(u?.email || r?.email || ''),
              phone: toStr(u?.phone || r?.phone || ''),
              panNumber: toStr(r?.panNumber || ''),
              aadhaarNumber: toStr(r?.aadhaarNumber || ''),
              bankName: toStr(r?.bankName || ''),
              bankAccount: toStr(r?.bankAccount || ''),
              ifscCode: toStr(r?.ifscCode || ''),
              status: toStr(r?.status || u?.kycStatus || 'pending'),
              submittedDate: fmtDate(r?.submittedAt || r?.createdAt),
              approvedDate: r?.approvedAt ? fmtDate(r?.approvedAt) : undefined,
              rejectedDate: r?.rejectedAt ? fmtDate(r?.rejectedAt) : undefined,
              isVerified: (r?.status || u?.kycStatus) === 'approved',
              progressPercent,
              documents: docObj,
              adminNotes: r?.adminNotes || ''
            };
          });
          setKycRecords(normalized);
          return;
        } catch { }
      }

      console.error('Error loading KYC records:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const uid = (user?.id || user?._id || '').toString();
            let rawList = [];
            try {
              const adminStatus = await ensureAdminAuth();
              if (adminStatus?.ok) {
                const resp = await api.kycApi.getKycRecords();
                rawList = Array.isArray(resp) ? resp : (Array.isArray(resp?.kycRecords) ? resp.kycRecords : []);
              } else if (uid) {
                const resp = await api.kycApi.getKycRecordByUserId(uid);
                rawList = Array.isArray(resp) ? resp : (resp ? [(resp.kyc || resp)] : []);
              }
            } catch { }
            const toStr = (v) => (v === null || v === undefined) ? '' : String(v);
            const fmtDate = (d) => { try { return new Date(d).toISOString().split('T')[0]; } catch { return ''; } };
            // Same file normalizer as main KYC loader
            const fileMeta = (val) => {
              if (!val) return null;
              if (typeof val === 'string') {
                const filename = val.split('/').pop() || 'document';
                return { url: val, filename, size: '', uploaded: true };
              }
              if (typeof val === 'object') {
                const rawUrl = val.url || val.path || val.location || '';
                const filename = val.filename || val.originalname || (typeof rawUrl === 'string' && rawUrl.split('/').pop()) || 'document';
                const size = val.sizeLabel || (val.size != null ? String(val.size) : '');
                return { url: rawUrl || '', filename, size, uploaded: true };
              }
              return null;
            };
            const normalized = rawList.map((r) => {
              const u = r?.userId && typeof r.userId === 'object' ? r.userId : {};
              const docs = r?.documents || {};
              const docObj = { aadhaar: fileMeta(docs.aadhaar), pan: fileMeta(docs.pan), bankStatement: fileMeta(docs.bankStatement), photo: docs.photo ? fileMeta(docs.photo) : null };
              const uploadedCount = Object.values(docObj).filter(d => d && d.uploaded).length;
              const progressPercent = Math.round((uploadedCount / 4) * 100);
              return {
                id: r?._id || r?.id || `kyc-${Date.now()}`,
                userId: ((u?._id || r?.userId || '') + '').toString(),
                userName: String(u?.name || r?.userName || 'User'),
                email: String(u?.email || r?.email || ''),
                phone: String(u?.phone || r?.phone || ''),
                panNumber: String(r?.panNumber || ''),
                aadhaarNumber: String(r?.aadhaarNumber || ''),
                bankName: String(r?.bankName || ''),
                bankAccount: String(r?.bankAccount || ''),
                ifscCode: String(r?.ifscCode || ''),
                status: String(r?.status || u?.kycStatus || 'pending'),
                submittedDate: (r?.submittedAt || r?.createdAt) ? new Date(r?.submittedAt || r?.createdAt).toISOString().split('T')[0] : '',
                approvedDate: r?.approvedAt ? new Date(r?.approvedAt).toISOString().split('T')[0] : undefined,
                rejectedDate: r?.rejectedAt ? new Date(r?.rejectedAt).toISOString().split('T')[0] : undefined,
                isVerified: (r?.status || u?.kycStatus) === 'approved',
                progressPercent,
                documents: docObj,
                adminNotes: r?.adminNotes || ''
              };
            });
            setKycRecords(normalized);
          } catch (retryError) {
            console.error('Retry failed for loading KYC records:', retryError);
            setKycRecords([]);
            addNotification({
              title: 'Error Loading KYC Records',
              message: 'Failed to load KYC records due to rate limiting. Please refresh the page.',
              type: 'error',
              audience: 'system'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      if ((kycRetryRef.current?.attempts || 0) < 3) {
        const d = kycRetryRef.current?.delay || 1000;
        try { if (kycRetryRef.current?.timer) clearTimeout(kycRetryRef.current.timer); } catch { }
        console.info('Scheduling KYC reload retry', { attempts: (kycRetryRef.current?.attempts || 0) + 1, delay: d });
        kycRetryRef.current.timer = setTimeout(() => {
          loadKycRecords();
        }, d);
        kycRetryRef.current.attempts = (kycRetryRef.current?.attempts || 0) + 1;
        kycRetryRef.current.delay = Math.min(d * 2, 8000);
        setLoadingKycRecords(true);
        return;
      }
      setKycRecords([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading KYC Records',
        message: (error?.status === 401 || error?.status === 403) ? 'You do not have permission to view all KYC records.' : 'Failed to load KYC verification data. Please try again later.',
        type: (error?.status === 401 || error?.status === 403) ? 'warning' : 'error',
        audience: 'system'
      });
    } finally {
      setLoadingKycRecords(false);
    }
  };

  // Load notifications from API
  const loadNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const notificationData = await api.notificationApi.getNotifications();
      let list = [];
      if (Array.isArray(notificationData)) {
        list = notificationData;
      } else if (Array.isArray(notificationData?.notifications)) {
        list = notificationData.notifications;
      }

      // Filter notifications based on user type
      // Users see: 'user' and 'all' targeted notifications
      // Admins see: 'admin' and 'all' targeted notifications
      const filtered = list.filter(n => {
        const audience = (n.targetAudience || n.audience || 'user').toLowerCase();
        if (userType === 'admin') {
          // Admins can see 'admin' and 'all' notifications
          return audience === 'admin' || audience === 'all';
        } else {
          // Users can see 'user' and 'all' notifications (exclude 'admin')
          return audience === 'user' || audience === 'all';
        }
      });

      setNotifications(filtered.map(normalizeNotification));
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const notificationData = await api.notificationApi.getNotifications();
            let list = [];
            if (Array.isArray(notificationData)) {
              list = notificationData;
            } else if (Array.isArray(notificationData?.notifications)) {
              list = notificationData.notifications;
            }

            // Apply same filtering as above
            const filtered = list.filter(n => {
              const audience = (n.targetAudience || n.audience || 'user').toLowerCase();
              if (userType === 'admin') {
                return audience === 'admin' || audience === 'all';
              } else {
                return audience === 'user' || audience === 'all';
              }
            });

            setNotifications(filtered.map(normalizeNotification));
          } catch (retryError) {
            console.error('Retry failed for loading notifications:', retryError);
            setNotifications([]);
            addNotification({
              title: 'Error Loading Notifications',
              message: 'Failed to load notifications due to rate limiting. Please refresh the page.',
              type: 'error',
              audience: 'system'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      setNotifications([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Notifications',
        message: 'Failed to load notifications. Please try again later.',
        type: 'error',
        audience: 'system'
      });
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Load gold rate from API
  const loadGoldRate = async () => {
    setLoadingGoldRate(true);
    try {
      const rateData = await api.goldRateApi.getCurrentRate();
      setGoldRate(rateData.rate);
    } catch (error) {
      // Silently handle 401 errors (gold rates endpoint is public, no auth required)
      if (error?.status === 401 || error?.message?.includes('Authentication required')) {
        // Use fallback rate silently for unauthenticated users
        setGoldRate(goldRateApiSettings.fallbackRate || 6850);
        return;
      }

      console.error('Error loading gold rate:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const rateData = await api.goldRateApi.getCurrentRate();
            setGoldRate(rateData.rate);
          } catch (retryError) {
            console.error('Retry failed for loading gold rate:', retryError);
            // Use fallback rate if API fails
            setGoldRate(goldRateApiSettings.fallbackRate || 6850);
            addNotification({
              title: 'Error Loading Gold Rate',
              message: 'Failed to load gold rate due to rate limiting. Using last known rate.',
              type: 'warning',
              audience: 'system'
            });
          }
        }, 3000); // Wait 3 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      // Use fallback rate if API fails
      setGoldRate(goldRateApiSettings.fallbackRate || 6850);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Gold Rate',
        message: 'Failed to load current gold rate. Using last known rate.',
        type: 'warning',
        audience: 'system'
      });
    } finally {
      setLoadingGoldRate(false);
    }
  };

  // Load referral data from API
  const loadReferralData = async () => {
    setLoadingReferrals(true);
    try {
      // Skip entirely if backend doesn't support referrals
      const ok = await supportsReferrals();
      if (!ok) {
        setReferralCode('');
        setReferralStats({});
        setReferralHistory([]);
        return;
      }

      // Get referral data from API
      const uid = user?.id || user?._id || null;
      const referralData = await api.referralApi.getReferralData(uid);
      setReferralCode(referralData.code || '');
      setReferralStats(referralData.stats || {});
      setReferralHistory(Array.isArray(referralData.history) ? referralData.history : []);
    } catch (error) {
      // Graceful degradation: if 404, fall back to stats-only endpoint and suppress console noise
      if (error?.status === 404 || /not\s*found/i.test(error?.message || '')) {
        try {
          const uid = user?.id || user?._id || null;
          const statsOnly = await api.referralApi.getReferralStats(uid);
          setReferralCode('');
          setReferralStats(statsOnly || {});
          setReferralHistory([]);
          return;
        } catch { }
      }
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const uid = user?.id || user?._id || null;
            const referralData = await api.referralApi.getReferralData(uid);
            setReferralCode(referralData.code || '');
            setReferralStats(referralData.stats || {});
            setReferralHistory(Array.isArray(referralData.history) ? referralData.history : []);
          } catch (retryError) {
            setReferralCode('');
            setReferralStats({});
            setReferralHistory([]);
          }
        }, 2000);
        return;
      }
      // Default: set safe empty state without logging
      setReferralCode('');
      setReferralStats({});
      setReferralHistory([]);
    } finally {
      setLoadingReferrals(false);
    }
  };

  // Load wallet transactions from API
  const loadWalletTransactions = async () => {
    setLoadingWalletTransactions(true);
    try {
      // Get wallet-specific transactions (ROI credits and withdrawals)
      const resp = await api.transactionApi.getTransactions();
      const allTransactions = Array.isArray(resp) ? resp : (Array.isArray(resp?.transactions) ? resp.transactions : []);
      const walletTxns = allTransactions.filter(txn =>
        txn?.type === 'roi_credit' || txn?.type === 'withdrawal'
      );
      setWalletTransactions(walletTxns);
    } catch (error) {
      console.error('Error loading wallet transactions:', error);
      // Handle rate limiting specifically
      if (error.message && error.message.includes('Too many requests')) {
        // Add a delay and retry once
        setTimeout(async () => {
          try {
            const allTransactions = await api.transactionApi.getTransactions();
            const walletTxns = allTransactions.filter(txn =>
              txn.type === 'roi_credit' || txn.type === 'withdrawal'
            );
            setWalletTransactions(walletTxns);
          } catch (retryError) {
            console.error('Retry failed for loading wallet transactions:', retryError);
            setWalletTransactions([]);
            addNotification({
              title: 'Error Loading Wallet Transactions',
              message: 'Failed to load wallet transactions due to rate limiting. Please refresh the page.',
              type: 'error',
              audience: 'system'
            });
          }
        }, 2000); // Wait 2 seconds before retry
        return; // Exit early to avoid setting loading to false immediately
      }
      setWalletTransactions([]);
      // Show user-friendly error notification
      addNotification({
        title: 'Error Loading Wallet Transactions',
        message: 'Failed to load wallet transaction history. Please try again later.',
        type: 'error',
        audience: 'system'
      });
    } finally {
      setLoadingWalletTransactions(false);
    }
  };

  // Runtime backend feature probe helpers (cache object is module-scoped above)
  const supportsReferrals = async () => {
    if (backendFeatures.referrals !== undefined) return backendFeatures.referrals;
    try {
      const info = await apiFetch(API_INFO_URL, { method: 'GET' });
      const has = !!(info && info.endpoints && (info.endpoints.Referrals || Object.values(info.endpoints).some((v) => (v || '').toString().includes('/referrals'))));
      backendFeatures.referrals = has;
      return has;
    } catch {
      backendFeatures.referrals = false;
      return false;
    }
  };

  // Detect whether backend supports key-value settings store (e.g., /settings/:key)
  const supportsSettingsKeyStore = async () => {
    if (backendFeatures.settingsKeyStore !== undefined) return backendFeatures.settingsKeyStore;
    try {
      const info = await apiFetch(API_INFO_URL, { method: 'GET' });
      const endpoints = info && info.endpoints ? info.endpoints : {};
      const has = !!(endpoints.Settings || Object.values(endpoints).some((v) => (v || '').toString().includes('/settings')));
      backendFeatures.settingsKeyStore = has;
      return has;
    } catch {
      backendFeatures.settingsKeyStore = false;
      return false;
    }
  };

  // Load all data with request staggering to prevent rate limiting
  const loadAllData = async () => {
    // Remove the duplicate useEffect that was causing multiple simultaneous requests
    // We'll only use the staggered loading approach here

    try {
      // Always load public data first
      await loadInvestmentPlans();
      await new Promise(resolve => setTimeout(resolve, 300)); // 300ms delay
      await loadGoldRate();

      // For protected endpoints, ensure we actually have a token
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Try bootstrap to reduce fan-out
      try {
        const boot = await api.dashboardApi.bootstrap(5);
        // Hydrate what we can from bootstrap
        if (Array.isArray(boot?.plans)) setInvestmentPlans(boot.plans.map(p => ({
          id: p._id || p.id || `plan-${Date.now()}`,
          title: p.name || p.title || '',
          description: p.description || '',
          minAmount: typeof p.minInvestment === 'number' ? p.minInvestment : (p.minAmount ?? 0),
          maxAmount: typeof p.maxInvestment === 'number' ? p.maxInvestment : (p.maxAmount ?? 0),
          roiPercentMonthly: typeof p.roiPercentage === 'number' ? p.roiPercentage : (p.roiPercentMonthly ?? 0),
          roiPercentAnnual: typeof p.roiPercentage === 'number' ? p.roiPercentage * 12 : (p.roiPercentAnnual ?? 0),
          durationMonths: p.durationMonths ?? p.duration ?? 0,
          active: typeof p.isActive === 'boolean' ? p.isActive : (p.active ?? true),
          color: p.color || 'bg-gradient-to-r from-blue-400 to-blue-600',
          tags: Array.isArray(p.tags) ? p.tags : []
        })));
        if (Array.isArray(boot?.notifications)) setNotifications(boot.notifications.map(normalizeNotification));
        if (Array.isArray(boot?.bankDetails)) setUserBankDetails(boot.bankDetails);
        if (boot?.kyc) setKycRecords([boot.kyc]);
        if (boot?.goldRate?.rate) setGoldRate(boot.goldRate.rate);
      } catch (e) {
        // ignore bootstrap failure and continue with granular calls
      }

      // Verify auth with profile before issuing any protected requests
      // Also load wallet balance from user profile
      try {
        const profile = await authService.getProfile();
        // Set wallet balance from user profile
        if (profile && typeof profile.walletBalance === 'number') {
          setWalletBalance(profile.walletBalance);
          console.log(`💰 Loaded wallet balance: ₹${profile.walletBalance}`);
        } else if (profile?.user && typeof profile.user.walletBalance === 'number') {
          setWalletBalance(profile.user.walletBalance);
          console.log(`💰 Loaded wallet balance: ₹${profile.user.walletBalance}`);
        }
      } catch (authErr) {
        if (authErr?.status === 401) return;
        console.warn('Profile check failed, proceeding cautiously:', authErr?.message || authErr);
      }

      // Load protected data with delays to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 300)); // 300ms delay
      await loadUserInvestments();

      // Load secondary protected data with longer delays
      try {
        const adminStatus = await ensureAdminAuth();
        if (adminStatus?.ok) {
          setTimeout(() => loadUsers && loadUsers(), 500);
        }
      } catch { }
      setTimeout(() => loadBankDetails(), 800);
      setTimeout(() => loadTransactions(), 1100);
      setTimeout(() => loadKycRecords(), 1400);
      setTimeout(() => loadNotifications(), 1700);
      supportsReferrals().then((ok) => { if (ok) setTimeout(() => loadReferralData(), 200); });
      setTimeout(() => loadWalletTransactions(), 2300);
    } catch (error) {
      console.error('Error in batch data loading:', error);
      addNotification({
        title: 'Data Loading Error',
        message: 'Some data failed to load. Please refresh the page.',
        type: 'error'
      });
    }
  };

  // Remove the duplicate useEffect that was causing multiple simultaneous requests
  // Keep only one useEffect for initial data loading
  useEffect(() => {
    // Only load data if user is authenticated
    if (user) {
      // Debounce initial load to prevent immediate rate limiting
      const timer = setTimeout(() => {
        loadAllData();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user]); // Add user as dependency

  // Initialize Socket.io connection when user is present; disconnect on logout
  useEffect(() => {
    let svc = null;
    let cancelled = false;
    (async () => {
      if (user) {
        try {
          if (cancelled) return;
          svc = socketService;
          svc.init();
        } catch { }
      }
    })();
    return () => {
      cancelled = true;
      if (svc) {
        try { svc.disconnect(); } catch { }
      }
    };
  }, [user]);

  // Social Login Settings with localStorage persistence
  const [socialLoginSettings, setSocialLoginSettings] = useState(() => {
    const saved = localStorage.getItem('mgx_social_login_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Invalid social login settings in localStorage, resetting to defaults:', e);
      }
    }
    // Default: social login disabled until explicitly configured in Admin Settings
    return {
      googleLoginEnabled: false,
      facebookLoginEnabled: false,
      googleClientId: '',
      googleClientSecret: '',
      facebookAppId: '',
      facebookAppSecret: ''
    };
  });

  // Persist social login settings to localStorage
  useEffect(() => {
    localStorage.setItem('mgx_social_login_settings', JSON.stringify(socialLoginSettings));
  }, [socialLoginSettings]);

  // Branding Settings
  const [brandingSettings, setBrandingSettings] = useState({
    panelName: 'Swarna Setu',
    logoUrl: '',
    faviconUrl: ''
  });

  // Fetch branding on app start (no auth required)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.settingsApi.getBranding();
        if (!cancelled && data) setBrandingSettings({
          panelName: data.panelName || 'Swarna Setu Digital Gold Investment Platform',
          logoUrl: data.logoUrl || '',
          faviconUrl: data.faviconUrl || ''
        });
      } catch { }
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply branding to document title and favicon
  useEffect(() => {
    try {
      if (brandingSettings?.panelName) {
        document.title = brandingSettings.panelName;
      }
      const ensureFavicon = (href) => {
        const doc = document;
        let link = doc.querySelector("link[rel='icon']");
        if (!link) {
          link = doc.createElement('link');
          link.setAttribute('rel', 'icon');
          doc.head.appendChild(link);
        }
        if (href) link.setAttribute('href', href);
      };
      if (brandingSettings?.faviconUrl) {
        ensureFavicon(brandingSettings.faviconUrl);
      }
    } catch { }
  }, [brandingSettings]);

  // Admin: update branding settings via API
  const updateBranding = async ({ panelName, logoFile, faviconFile } = {}) => {
    try {
      const updated = await api.settingsApi.updateBranding({ panelName, logoFile, faviconFile });
      setBrandingSettings({
        panelName: updated.panelName || panelName || brandingSettings.panelName,
        logoUrl: updated.logoUrl || brandingSettings.logoUrl,
        faviconUrl: updated.faviconUrl || brandingSettings.faviconUrl,
      });
      return { success: true, data: updated };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to update branding' };
    }
  };

  // Gold Rate API Settings with localStorage persistence and migration
  const [goldRateApiSettings, setGoldRateApiSettings] = useState(() => {
    const saved = localStorage.getItem('mgx_gold_rate_api_settings');
    let parsed = saved ? JSON.parse(saved) : {};

    // Migration: Ensure providers object exists with all required providers
    if (!parsed.providers) {
      parsed.providers = {};
    }

    // Ensure all providers exist with default values
    const defaultProviders = {
      goldapi: {
        enabled: false,
        name: 'GoldAPI.io',
        apiKey: '',
        endpoint: `${API_BASE}/gold-rates/proxy/goldapi?symbol=XAU&base=INR`,
        authMethod: 'query',
        authHeader: 'x-access-token',
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        responseFormat: 'price',
        currency: 'INR',
        priceUnit: 'oz' // price per troy ounce; will normalize to per-gram/per-10g
      },
      metalpriceapi: {
        enabled: false,
        name: 'MetalPriceAPI.com',
        apiKey: '',
        endpoint: `${API_BASE}/gold-rates/proxy/metalpriceapi`,
        symbols: 'INR',
        base: 'XAU',
        authMethod: 'query',
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        responseFormat: 'rates.INR',
        currency: 'INR',
        priceUnit: 'oz' // XAU -> currency returns per troy ounce
      },
      metalsdev: {
        enabled: false,
        name: 'Metals.dev',
        apiKey: '',
        endpoint: `${API_BASE}/gold-rates/proxy/metalsdev`,
        method: 'GET',
        contentType: 'application/json',
        authMethod: 'query',
        responseFormat: 'custom',
        responsePath: 'gold', // Metals.dev returns { gold: price_in_INR_per_gram }
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        currency: 'INR',
        priceUnit: 'gram'
      },
      bullionvault: {
        enabled: false,
        name: 'BullionVault API',
        apiKey: '',
        endpoint: 'https://www.bullionvault.com/api/v2/prices',
        authMethod: 'bearer',
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        responseFormat: 'gold.price',
        currency: 'INR',
        priceUnit: 'gram'
      },
      goldapicom: {
        enabled: false,
        name: 'Gold-API.com',
        apiKey: '',
        endpoint: `${API_BASE}/gold-rates/proxy/goldapicom?symbol=XAU`,
        authMethod: 'query',
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        responseFormat: 'price',
        currency: 'USD',
        priceUnit: 'oz' // price per troy ounce in USD
      },
      custom: {
        enabled: false,
        name: 'Custom API',
        apiKey: '',
        endpoint: '',
        headers: {},
        responsePath: 'rate',
        authMethod: 'bearer',
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        responseFormat: 'custom',
        currency: 'INR',
        priceUnit: 'gram'
      },
      local: {
        enabled: false,
        name: 'Local API',
        endpoint: '',
        apiKey: '',
        authMethod: 'bearer',
        lastRate: null,
        lastUpdate: null,
        status: 'inactive',
        errorCount: 0,
        lastError: null,
        responseFormat: 'rate',
        currency: 'INR',
        priceUnit: 'gram'
      }
    };

    // Merge existing providers with defaults
    parsed.providers = { ...defaultProviders, ...parsed.providers };

    // Migration: ensure proxy endpoints are absolute to backend API (not relative to frontend host)
    Object.keys(parsed.providers).forEach((key) => {
      const p = parsed.providers[key];
      if (p && typeof p.endpoint === 'string' && p.endpoint.startsWith('/api/')) {
        p.endpoint = `${API_BASE}${p.endpoint.replace(/^\/api\/v1?/, '/api/v1')}`;
      }
    });

    // Ensure other required fields exist
    return {
      apiEnabled: parsed.apiEnabled || false,
      primaryProvider: parsed.primaryProvider || 'goldapi',
      providers: parsed.providers,
      updateInterval: parsed.updateInterval || 300,
      lastUpdate: parsed.lastUpdate || null,
      fallbackRate: parsed.fallbackRate || 6850,
      errorRetryCount: parsed.errorRetryCount || 3,
      errorRetryDelay: parsed.errorRetryDelay || 60,
      enableRealTimeUpdates: parsed.enableRealTimeUpdates !== undefined ? parsed.enableRealTimeUpdates : true,
      publishAutoUpdates: parsed.publishAutoUpdates !== undefined ? parsed.publishAutoUpdates : true,
      lastPublishedAt: parsed.lastPublishedAt || null,
      supportedCurrencies: parsed.supportedCurrencies || ['INR'],
      rateSource: parsed.rateSource || 'manual',
      failoverEnabled: parsed.failoverEnabled !== undefined ? parsed.failoverEnabled : true,
      rateHistory: parsed.rateHistory || [],
      maxHistorySize: parsed.maxHistorySize || 100,
      currencyConversion: parsed.currencyConversion || {
        provider: 'exchangerateapi',
        apiKey: '',
        cacheTTL: 300, // seconds
        lastUsdInr: null,
        lastFetchedAt: null
      }
    };
  });

  // Persist gold rate API settings to localStorage
  useEffect(() => {
    localStorage.setItem('mgx_gold_rate_api_settings', JSON.stringify(goldRateApiSettings));
  }, [goldRateApiSettings]);

  // Function to update social login settings with validation
  const updateSocialLoginSettings = (newSettings) => {
    setSocialLoginSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };

  // Function to update gold rate - fetches fresh rate and saves to database
  const updateGoldRate = async (newRate) => {
    const token = localStorage.getItem('auth_token');

    // Update local state immediately
    setGoldRate(newRate);
    setGoldRateApiSettings(prev => ({
      ...prev,
      rateSource: 'manual'
    }));

    // Check authentication
    if (!token) {
      addNotification({
        title: 'Gold Rate Updated (Local Only)',
        message: 'You are not logged in. Rate updated locally for this session.',
        type: 'warning'
      });
      return { savedToServer: false, localApplied: true, reason: 'unauthenticated' };
    }

    // Check if admin
    let isAdmin = false;
    try {
      const adminStatus = await ensureAdminAuth();
      isAdmin = !!adminStatus?.ok;
    } catch { }

    if (!isAdmin) {
      addNotification({
        title: 'Gold Rate Updated (Local Only)',
        message: 'Admin access required to save rates to server.',
        type: 'warning'
      });
      return { savedToServer: false, localApplied: true, reason: 'not_admin' };
    }

    // Fetch fresh rate from backend which automatically saves to database
    try {
      const response = await fetch(`${API_BASE}/gold-price`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.price_per_gram) {
        // Backend automatically saves to database when fetching
        setGoldRate(data.price_per_gram);
        addNotification({
          title: 'Gold Rate Published',
          message: `Rate saved to database: ₹${Math.round(data.price_per_gram * 10).toLocaleString()}/10g`,
          type: 'success'
        });
        return { savedToServer: true, localApplied: true, rate: data.price_per_gram };
      } else {
        throw new Error('Invalid response from backend');
      }
    } catch (error) {
      console.error('Failed to save gold rate:', error);
      addNotification({
        title: 'Save Failed',
        message: 'Could not save rate to server. Rate applied locally only.',
        type: 'error'
      });
      return { savedToServer: false, localApplied: true, reason: 'backend_error', error: error.message };
    }
  };

  // Function to update gold rate API settings
  const updateGoldRateApiSettings = (newSettings) => {
    setGoldRateApiSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  };

  // Helper: fetch USD->INR rate with caching and selectable provider
  const getUsdToInrRate = async () => {
    try {
      // Return cached if fresh
      if (goldRateApiSettings.currencyConversion.lastUsdInr && goldRateApiSettings.currencyConversion.lastFetchedAt) {
        const ageSec = (Date.now() - new Date(goldRateApiSettings.currencyConversion.lastFetchedAt).getTime()) / 1000;
        if (ageSec < (goldRateApiSettings.currencyConversion.cacheTTL || 300)) {
          return goldRateApiSettings.currencyConversion.lastUsdInr;
        }
      }

      const { provider, apiKey } = goldRateApiSettings.currencyConversion || {};
      let rate = null;

      if (provider === 'exchangerateapi') {
        if (apiKey) {
          const url = `${API_BASE}/gold-rates/proxy/usd-inr?provider=exchangerateapi&api_key=${encodeURIComponent(apiKey)}`;
          const data = await apiFetch(url, { method: 'GET' });
          rate = data?.rate;
        } else {
          // Direct keyless fallback with wide CORS support
          const resp = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=INR', { headers: { 'Accept': 'application/json' } });
          const data = await resp.json();
          rate = data?.rates?.INR;
        }
      } else if (provider === 'fixer') {
        if (apiKey) {
          const url = `${API_BASE}/gold-rates/proxy/usd-inr?provider=fixer&api_key=${encodeURIComponent(apiKey)}`;
          const data = await apiFetch(url, { method: 'GET' });
          rate = data?.rate;
        } else {
          // Fallback to keyless provider when Fixer key is not set
          const resp = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=INR', { headers: { 'Accept': 'application/json' } });
          const data = await resp.json();
          rate = data?.rates?.INR;
        }
      }

      if (!rate || typeof rate !== 'number') {
        throw new Error('Failed to fetch USD->INR rate');
      }

      // Persist cache
      setGoldRateApiSettings(prev => ({
        ...prev,
        currencyConversion: {
          ...prev.currencyConversion,
          lastUsdInr: rate,
          lastFetchedAt: new Date().toISOString()
        }
      }));

      return rate;
    } catch (err) {
      console.error('Currency conversion fetch failed:', err);
      // Fallback to last cached
      if (goldRateApiSettings.currencyConversion?.lastUsdInr) {
        return goldRateApiSettings.currencyConversion.lastUsdInr;
      }
      throw err;
    }
  };

  // Helper: normalize metal price to INR per gram and per 10g
  const normalizeToInr = async ({ value, currency = 'INR', unit = 'gram' }) => {
    let perGramINR;
    if (currency === 'INR') {
      if (unit === 'oz') {
        perGramINR = value / 31.1034768; // INR per oz -> INR per gram
      } else if (unit === 'gram') {
        perGramINR = value;
      } else if (unit === 'kg') {
        perGramINR = value / 1000;
      } else {
        perGramINR = value; // best-effort
      }
    } else if (currency === 'USD') {
      const usdInr = await getUsdToInrRate();
      let usdPerGram;
      if (unit === 'oz') {
        usdPerGram = value / 31.1034768;
      } else if (unit === 'gram') {
        usdPerGram = value;
      } else if (unit === 'kg') {
        usdPerGram = value / 1000;
      } else {
        usdPerGram = value;
      }
      perGramINR = usdPerGram * usdInr;
    } else {
      // Unsupported source currency, return as-is
      perGramINR = value;
    }
    return { perGram: perGramINR, per10g: perGramINR * 10 };
  };

  // Function to fetch gold rate from a specific provider
  const fetchFromProvider = async (providerKey) => {
    const provider = goldRateApiSettings.providers[providerKey];
    if (!provider || !provider.enabled) {
      return { success: false, error: 'Provider not enabled' };
    }

    try {
      // Special handling for goldapicom - use backend service directly
      if (providerKey === 'goldapicom') {
        try {
          console.log('🔄 Fetching gold rate from backend service (Gold-API.com)...');

          // Call the backend service that uses Gold-API.com
          const token = localStorage.getItem('auth_token');
          const headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          };

          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }

          const response = await fetch(`${API_BASE}/gold-price`, {
            method: 'GET',
            headers
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(errorData.message || `Backend service error: ${response.status}`);
          }

          const data = await response.json();

          // Backend returns: { success, price_per_gram, price_24k, price_22k, karats, ... }
          const perGram = data.price_per_gram || data.rate;

          if (!perGram || typeof perGram !== 'number') {
            throw new Error('Invalid rate data from backend service');
          }

          const per10g = Math.round(perGram * 10);
          const timestamp = new Date().toISOString();

          setGoldRateApiSettings(prev => ({
            ...prev,
            providers: {
              ...prev.providers,
              [providerKey]: {
                ...prev.providers[providerKey],
                lastRate: per10g,
                lastUpdate: timestamp,
                status: 'active',
                errorCount: 0,
                lastError: null
              }
            },
            rateHistory: [
              { provider: providerKey, rate: per10g, timestamp, source: 'backend_service' },
              ...prev.rateHistory.slice(0, prev.maxHistorySize - 1)
            ]
          }));

          return { success: true, rate: per10g, ratePerGram: perGram, ratePer10g: per10g, provider: providerKey };
        } catch (backendError) {
          console.error('Backend service failed, trying database fallback:', backendError.message);

          // Fallback to database rate
          try {
            const db = await fetch(`${API_BASE}/gold-rates`, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
            }).then(res => res.json());

            const perGram = Number(db?.rate);
            if (perGram && !Number.isNaN(perGram)) {
              const per10g = perGram * 10;
              const timestamp = new Date().toISOString();

              setGoldRateApiSettings(prev => ({
                ...prev,
                providers: {
                  ...prev.providers,
                  [providerKey]: {
                    ...prev.providers[providerKey],
                    lastRate: per10g,
                    lastUpdate: timestamp,
                    status: 'fallback',
                    errorCount: 0,
                    lastError: 'Using database fallback'
                  }
                },
                rateHistory: [
                  { provider: providerKey, rate: per10g, timestamp, source: 'db_fallback' },
                  ...prev.rateHistory.slice(0, prev.maxHistorySize - 1)
                ]
              }));

              return { success: true, rate: per10g, ratePerGram: perGram, ratePer10g: per10g, provider: providerKey };
            }
          } catch (dbError) {
            console.error('Database fallback also failed:', dbError.message);
          }

          throw backendError;
        }
      }

      // If GoldAPI.io has no API key configured, attempt DB fallback first to avoid proxy 403s
      if (providerKey === 'goldapi' && (!provider.apiKey || !provider.apiKey.trim())) {
        try {
          // Use apiFetch without auth for public endpoint
          const db = await fetch(`${API_BASE}/gold-rates`, {
            method: 'GET',
            headers: { 'Accept': 'application/json' }
          }).then(res => res.json());
          const perGram = Number(db?.rate);
          if (perGram && !Number.isNaN(perGram)) {
            const per10g = perGram * 10;
            const timestamp = new Date().toISOString();
            setGoldRateApiSettings(prev => ({
              ...prev,
              providers: {
                ...prev.providers,
                [providerKey]: {
                  ...prev.providers[providerKey],
                  lastRate: per10g,
                  lastUpdate: timestamp,
                  status: 'fallback',
                  errorCount: 0,
                  lastError: null
                }
              },
              rateHistory: [
                { provider: providerKey, rate: per10g, timestamp, source: 'db_fallback' },
                ...prev.rateHistory.slice(0, prev.maxHistorySize - 1)
              ]
            }));
            return { success: true, rate: per10g, ratePerGram: perGram, ratePer10g: per10g, provider: providerKey };
          }
        } catch { }
      }

      // Use the provider's configured endpoint
      let url = provider.endpoint;

      // Ensure we have a valid URL
      if (!url) {
        throw new Error('Provider endpoint not configured');
      }

      const headers = {};

      // Configure authentication based on provider settings
      switch (provider.authMethod) {
        case 'header':
          if (provider.apiKey && provider.authHeader) {
            headers[provider.authHeader] = provider.apiKey;
          }
          break;
        case 'query':
          {
            const isPlaceholderKey = typeof provider.apiKey === 'string' && /^(goldapi-|demo-|placeholder)/i.test(provider.apiKey);
            const hasKey = !!provider.apiKey && !isPlaceholderKey;
            const separator = url.includes('?') ? '&' : '?';
            if (hasKey) {
              url += `${separator}api_key=${encodeURIComponent(provider.apiKey)}`;
            }
            // Preserve base/symbols if provided
            const qp = [];
            if (provider.base) qp.push(`base=${encodeURIComponent(provider.base)}`);
            if (provider.symbols) qp.push(`symbols=${encodeURIComponent(provider.symbols)}`);
            if (qp.length) url += (url.includes('?') ? '&' : '?') + qp.join('&');
          }
          break;
        case 'bearer':
          if (provider.apiKey) {
            headers['Authorization'] = `Bearer ${provider.apiKey}`;
          }
          break;
      }

      // Add custom headers for custom provider
      if (providerKey === 'custom' && provider.headers) {
        Object.assign(headers, provider.headers);
      }

      // Single request via apiFetch (no default Content-Type on GET)
      const data = await apiFetch(url, {
        method: 'GET',
        headers
      });

      let newRate = null;

      // 1) Prefer explicit responsePath when provided (dot-notation)
      if (provider.responsePath && typeof provider.responsePath === 'string') {
        const path = provider.responsePath.split('.');
        newRate = path.reduce((obj, key) => (obj != null ? obj[key] : undefined), data);
      }

      // 2) If not found yet, support responseFormat as a dot-notation path (e.g., 'rates.INR')
      if (newRate == null && typeof provider.responseFormat === 'string' && provider.responseFormat.includes('.')) {
        const path = provider.responseFormat.split('.');
        newRate = path.reduce((obj, key) => (obj != null ? obj[key] : undefined), data);
      }

      // 3) Known format shorthands and fallbacks
      if (newRate == null) {
        switch (provider.responseFormat) {
          case 'price':
            newRate = data.price || data.rate || data.value;
            break;
          case 'rates.XAU': // backward-compat: some configs expect XAU under rates
            newRate = data.rates && data.rates.XAU ? data.rates.XAU : null;
            break;
          case 'gold.price':
            newRate = data.gold && data.gold.price ? data.gold.price : null;
            break;
          case 'custom':
            if (provider.responsePath) {
              const path = provider.responsePath.split('.');
              newRate = path.reduce((obj, key) => (obj != null ? obj[key] : undefined), data);
            } else {
              newRate = data.rate || data.price || data.value;
            }
            break;
          default:
            newRate = data.rate || data.price || data.value || data.goldRate;
        }
      }

      if (newRate && typeof newRate === 'number') {
        // Normalize to INR per gram and per 10g based on provider currency/unit
        const currency = provider.currency || 'INR';
        const unit = provider.priceUnit || 'gram';
        const { perGram, per10g } = await normalizeToInr({ value: newRate, currency, unit });
        const rateForUI = per10g; // use per 10g for UI displays
        const timestamp = new Date().toISOString();

        // Store historical rate data
        const historicalEntry = {
          provider: providerKey,
          rate: rateForUI,
          timestamp,
          source: 'api'
        };

        // Update provider status and store rate
        setGoldRateApiSettings(prev => ({
          ...prev,
          providers: {
            ...prev.providers,
            [providerKey]: {
              ...prev.providers[providerKey],
              lastRate: rateForUI,
              lastUpdate: timestamp,
              status: 'active',
              errorCount: 0,
              lastError: null
            }
          },
          rateHistory: [
            historicalEntry,
            ...prev.rateHistory.slice(0, prev.maxHistorySize - 1)
          ]
        }));

        return { success: true, rate: rateForUI, ratePerGram: perGram, ratePer10g: per10g, provider: providerKey };
      } else {
        throw new Error('Invalid rate data received from API');
      }

    } catch (error) {
      console.error(`Failed to fetch from ${providerKey}:`, error);

      // Friendlier error mapping
      let friendly = error?.message || 'Unknown error';
      if (/404|Not Found/i.test(friendly)) {
        friendly = providerKey === 'goldapicom'
          ? 'Gold-API.com returned 404; this provider may be unsupported or down. Enable GoldAPI.io or MetalPriceAPI.'
          : 'Provider endpoint returned 404. Check endpoint and API key (use proxy).';
      } else if (/CORS|Network/i.test(friendly)) {
        friendly = 'Network/CORS blocked direct call. Use backend proxy endpoints only.';
      }

      // Update provider error status
      setGoldRateApiSettings(prev => ({
        ...prev,
        providers: {
          ...prev.providers,
          [providerKey]: {
            ...prev.providers[providerKey],
            status: 'error',
            errorCount: prev.providers[providerKey].errorCount + 1,
            lastError: friendly
          }
        }
      }));

      return { success: false, error: friendly, provider: providerKey };
    }
  };

  // Simplified: Fetch gold rate from backend (using new clean API)
  const fetchGoldRateFromApi = async () => {
    try {
      console.log('📊 Fetching fresh gold rate from backend...');

      // Use new clean gold price API
      const result = await api.goldPriceApi.getLatestPrice();

      if (result.success && result.rate) {
        const perGram = result.rate;
        const per10g = result.rate_per_10g || Math.round(perGram * 10);

        // Update local state
        setGoldRate(perGram);

        setGoldRateApiSettings(prev => ({
          ...prev,
          lastUpdate: new Date().toISOString(),
          rateSource: 'api'
        }));

        return {
          success: true,
          rate: per10g,
          ratePerGram: perGram,
          ratePer10g: per10g,
          provider: 'backend'
        };
      }

      throw new Error('Failed to fetch gold rate');
    } catch (error) {
      console.error('⚠️ Gold rate fetch failed:', error);

      // Fallback to configured fallback rate
      if (goldRateApiSettings.fallbackRate && goldRateApiSettings.fallbackRate !== goldRate) {
        setGoldRate(goldRateApiSettings.fallbackRate);
        setGoldRateApiSettings(prev => ({
          ...prev,
          lastUpdate: new Date().toISOString(),
          rateSource: 'fallback'
        }));
        return {
          success: true,
          rate: goldRateApiSettings.fallbackRate,
          source: 'fallback'
        };
      }

      return {
        success: false,
        error: 'Failed to fetch gold rate and no fallback available'
      };
    }
  };

  // Auto-update gold rate based on interval
  useEffect(() => {
    if (!goldRateApiSettings.apiEnabled || !goldRateApiSettings.enableRealTimeUpdates) {
      return;
    }

    // Ensure minimum interval of 60 seconds to prevent rate limiting
    const intervalSeconds = Math.max(60, goldRateApiSettings.updateInterval || 300);

    const interval = setInterval(() => {
      fetchGoldRateFromApi();
    }, intervalSeconds * 1000);

    return () => clearInterval(interval);
  }, [goldRateApiSettings.apiEnabled, goldRateApiSettings.enableRealTimeUpdates, goldRateApiSettings.updateInterval]);

  // Push Notification Settings and State
  const [pushNotificationSettings, setPushNotificationSettings] = useState({
    enabled: false,
    fcmConfigured: true, // Sync with nested push.enableFCM
    apnsConfigured: false, // Sync with nested push.enableAPNS
    webPushConfigured: false, // Sync with nested push.enableWebPush
    defaultPriority: 'normal',
    ttl: 2419200, // 28 days
    enableAnalytics: true,
    enableDeliveryTracking: true,
    enableClickTracking: false,
    vapidPublicKey: '',
    vapidPrivateKey: '',
    fcmServerKey: '',
    apnsKeyId: '',
    apnsTeamId: '',
    apnsBundleId: '',
    apnsEnvironment: 'production',
    // Nested structure for PushSettingsModal compatibility
    push: {
      enableFCM: true,
      enableAPNS: false,
      defaultPriority: 'normal',
      defaultTTL: 2419200,
      fcmServerKey: '',
      fcmServiceAccount: '',
      fcmProjectId: '',
      apnsPrivateKey: '',
      apnsKeyId: '',
      apnsTeamId: '',
      apnsBundleId: '',
      apnsEnvironment: 'sandbox',
      enableWebPush: false,
      vapidPublicKey: '',
      vapidPrivateKey: '',
      vapidSubject: '',
      enableAnalytics: true,
      enableDeliveryReports: true,
      enableRichNotifications: false,
      enableSilentNotifications: false,
      enableGrouping: false
    }
  });

  const [pushNotificationHistory, setPushNotificationHistory] = useState([
    {
      id: 'push-1',
      notificationId: 'notif-1',
      platform: 'fcm',
      status: 'delivered',
      sentAt: '2024-08-25T10:30:00Z',
      deliveredAt: '2024-08-25T10:30:05Z',
      deviceToken: 'fcm-device-token-123',
      errorMessage: null
    },
    {
      id: 'push-2',
      notificationId: 'notif-2',
      platform: 'apns',
      status: 'delivered',
      sentAt: '2024-08-24T14:15:00Z',
      deliveredAt: '2024-08-24T14:15:02Z',
      deviceToken: 'apns-device-token-456',
      errorMessage: null
    }
  ]);

  // Bank Account Management Functions
  const addBankAccount = async (bankAccount) => {
    try {
      // Remove frontend-only fields and prepare payload for backend
      const { id, dateAdded, kycCompliant, confirmAccountNumber, ...rest } = bankAccount;

      // Helpers to normalize enums to backend expectations
      const normalizeStatus = (s) => {
        const v = (s || '').toString().toLowerCase();
        if (v === 'pending') return 'Pending';
        if (v === 'verified' || v === 'approved') return 'Verified';
        if (v === 'rejected') return 'Rejected';
        return undefined;
      };
      const normalizeAccountType = (t) => {
        const v = (t || '').toString().toLowerCase();
        if (v.includes('current')) return 'Current';
        // treat salary/savings/empty as Savings
        return 'Savings';
      };

      // Normalize and map fields to backend schema, supporting both user and admin forms
      const payload = {
        bankName: rest.bankName,
        accountHolderName: rest.accountHolderName,
        accountNumber: rest.accountNumber,
        ifscCode: rest.ifscCode?.toUpperCase(),
        accountType: normalizeAccountType(rest.accountType),
        // support both keys
        branchName: rest.branchName ?? rest.branch ?? '',
        panNumber: rest.panNumber?.toUpperCase() || '',
        mobileNumber: rest.mobileNumber || '',
        emailAddress: rest.emailAddress || '',
        micr: rest.micr ?? rest.micrCode ?? '',
        status: normalizeStatus(rest.status) || 'Pending',
      };

      // Include userId for backward-compatible servers that still expect it in body
      const uid = (user?.id || user?._id || '').toString();
      if (uid) payload.userId = uid;

      console.log('📤 Sending bank account data:', payload);
      console.log('📋 Removed fields:', { id, dateAdded, kycCompliant, confirmAccountNumber });

      // Save to API
      const savedAccount = await api.bankApi.createBankDetail(payload);

      const docs = rest?.documents || {};
      const hasPresignedUrls = !!(docs.passbook?.url || docs.statement?.url || docs.cancelledCheque?.url);
      const hasDocs = !!(docs.passbook?.file || docs.statement?.file || docs.cancelledCheque?.file);
      let withDocs = savedAccount;
      if ((hasPresignedUrls || hasDocs) && (savedAccount?._id || savedAccount?.id)) {
        const idForDocs = savedAccount._id || savedAccount.id;
        try {
          if (hasPresignedUrls) {
            const payload = {
              bankPassbookUrl: docs.passbook?.url,
              bankStatementUrl: docs.statement?.url,
              cancelledChequeUrl: docs.cancelledCheque?.url,
            };
            withDocs = await api.bankApi.setDocumentUrls(idForDocs, payload);
          } else {
            const fd = new FormData();
            if (docs.passbook?.file) fd.append('passbook', docs.passbook.file, docs.passbook.name || 'passbook');
            if (docs.statement?.file) fd.append('statement', docs.statement.file, docs.statement.name || 'statement');
            if (docs.cancelledCheque?.file) fd.append('cancelledCheque', docs.cancelledCheque.file, docs.cancelledCheque.name || 'cancelledCheque');
            withDocs = await api.bankApi.uploadBankDocuments(idForDocs, fd);
          }
        } catch (e) { }
      }

      console.log('✅ Bank account created:', withDocs);

      // Map backend response to frontend format
      const accountToAdd = {
        ...withDocs,
        id: withDocs._id || withDocs.id,
        createdAt: withDocs.createdAt || new Date().toISOString()
      };

      setUserBankDetails(prev => [
        ...((Array.isArray(prev) ? prev : [])),
        accountToAdd
      ]);

      // Add notification for new bank account
      addNotification({
        title: 'Bank Account Added',
        message: `${bankAccount.bankName} account has been added successfully. Verification in progress.`,
        type: 'info',
        emailEnabled: true // Send email for bank account additions
      });
    } catch (error) {
      console.error('❌ Error adding bank account:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      let errorMessage = 'Failed to add bank account. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      addNotification({
        title: 'Error Adding Bank Account',
        message: errorMessage,
        type: 'error'
      });
      throw error;
    }
  };

  const updateBankAccount = async (id, updatedAccount) => {
    try {
      // Remove frontend-only fields and normalize
      const { kycCompliant, dateAdded, confirmAccountNumber, _id, id: localId, ...rest } = updatedAccount;

      const normalizeStatus = (s) => {
        const v = (s || '').toString().toLowerCase();
        if (v === 'pending') return 'Pending';
        if (v === 'verified' || v === 'approved') return 'Verified';
        if (v === 'rejected') return 'Rejected';
        return undefined;
      };
      const normalizeAccountType = (t) => {
        const v = (t || '').toString().toLowerCase();
        if (v.includes('current')) return 'Current';
        return 'Savings';
      };

      const payload = {
        bankName: rest.bankName,
        accountHolderName: rest.accountHolderName,
        accountNumber: rest.accountNumber,
        ifscCode: rest.ifscCode?.toUpperCase(),
        accountType: normalizeAccountType(rest.accountType),
        branchName: rest.branchName ?? rest.branch ?? '',
        panNumber: rest.panNumber?.toUpperCase() || '',
        mobileNumber: rest.mobileNumber || '',
        emailAddress: rest.emailAddress || '',
        micr: rest.micr ?? rest.micrCode ?? '',
        status: normalizeStatus(rest.status),
      };

      // Update in API (use _id if available, otherwise use id)
      const backendId = _id || id || localId;
      let savedAccount = await api.bankApi.updateBankDetail(backendId, payload);

      const docs = rest?.documents || {};
      const hasPresignedUrls = !!(docs.passbook?.url || docs.statement?.url || docs.cancelledCheque?.url);
      const hasDocs = !!(docs.passbook?.file || docs.statement?.file || docs.cancelledCheque?.file);
      if (hasPresignedUrls || hasDocs) {
        try {
          if (hasPresignedUrls) {
            const payload = {
              bankPassbookUrl: docs.passbook?.url,
              bankStatementUrl: docs.statement?.url,
              cancelledChequeUrl: docs.cancelledCheque?.url,
            };
            savedAccount = await api.bankApi.setDocumentUrls(backendId, payload);
          } else {
            const fd = new FormData();
            if (docs.passbook?.file) fd.append('passbook', docs.passbook.file, docs.passbook.name || 'passbook');
            if (docs.statement?.file) fd.append('statement', docs.statement.file, docs.statement.name || 'statement');
            if (docs.cancelledCheque?.file) fd.append('cancelledCheque', docs.cancelledCheque.file, docs.cancelledCheque.name || 'cancelledCheque');
            savedAccount = await api.bankApi.uploadBankDocuments(backendId, fd);
          }
        } catch (e) { }
      }

      // Map backend response to frontend format
      const accountToUpdate = {
        ...savedAccount,
        id: savedAccount._id || savedAccount.id
      };

      setUserBankDetails(prev =>
        prev.map(account =>
          (account.id === id || account._id === backendId)
            ? accountToUpdate
            : account
        )
      );

      // Add notification for bank account update
      addNotification({
        title: 'Bank Account Updated',
        message: `${updatedAccount.bankName} account details have been updated successfully.`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating bank account:', error);
      addNotification({
        title: 'Error Updating Bank Account',
        message: error.response?.data?.message || 'Failed to update bank account. Please try again.',
        type: 'error'
      });
    }
  };

  const deleteBankAccount = async (accountId) => {
    try {
      const accountToDelete = (Array.isArray(userBankDetails) ? userBankDetails : []).find(acc => (acc.id === accountId || acc._id === accountId));

      // Determine backend id
      const backendId = accountToDelete?._id || accountId;

      // Delete from API
      await api.bankApi.deleteBankDetail(backendId);

      setUserBankDetails(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        const filtered = arr.filter(acc => (acc.id !== accountId && acc._id !== backendId));
        if (accountToDelete?.isPrimary && filtered.length > 0) {
          // If deleting primary account, make the first remaining account primary
          filtered[0] = { ...filtered[0], isPrimary: true };
        }
        return filtered;
      });

      // Add notification for bank account deletion
      if (accountToDelete) {
        addNotification({
          title: 'Bank Account Removed',
          message: `${accountToDelete.bankName} account has been removed from your profile.`,
          type: 'info'
        });
      }
    } catch (error) {
      console.error('Error deleting bank account:', error);
      addNotification({
        title: 'Error Removing Bank Account',
        message: 'Failed to remove bank account. Please try again.',
        type: 'error'
      });
    }
  };

  const setPrimaryBankAccount = async (accountId) => {
    try {
      const target = (Array.isArray(userBankDetails) ? userBankDetails : []).find(acc => (acc.id === accountId || acc._id === accountId));
      const backendId = target?._id || accountId;
      // Update in API
      await api.bankApi.setPrimaryBankAccount(backendId);

      setUserBankDetails(prev =>
        (Array.isArray(prev) ? prev : []).map(account => ({
          ...account,
          isPrimary: (account.id === accountId || account._id === backendId)
        }))
      );

      const primaryAccount = (Array.isArray(userBankDetails) ? userBankDetails : []).find(acc => (acc.id === accountId || acc._id === backendId));
      if (primaryAccount) {
        addNotification({
          title: 'Primary Account Updated',
          message: `${primaryAccount.bankName} account is now set as your primary account.`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error setting primary bank account:', error);
      addNotification({
        title: 'Error Setting Primary Account',
        message: 'Failed to set primary bank account. Please try again.',
        type: 'error'
      });
    }
  };

  // Transaction functions
  const addTransaction = async (transaction, options = {}) => {
    try {
      const newTransaction = {
        ...transaction,
        id: `txn-${Date.now()}`,
        timestamp: new Date().toISOString()
      };

      // Persist minimal required fields to API
      const uid = transaction.userId || (user?.id || user?._id || '').toString();
      const payload = {
        userId: uid,
        type: transaction.type,
        amount: Number(transaction.amount) || 0,
        description: transaction.description || 'Transaction'
      };

      const savedTransaction = await api.transactionApi.createTransaction(payload);
      setTransactions(prev => [savedTransaction, ...prev]);

      // Optionally approve immediately (mark as completed)
      if (options.approve && (savedTransaction?._id || savedTransaction?.id)) {
        const tid = savedTransaction._id || savedTransaction.id;
        try {
          await api.transactionApi.approveTransaction(tid);
          // Reflect approved status locally
          setTransactions(prev => prev.map(txn => {
            const id = txn._id || txn.id;
            return id === tid ? { ...txn, status: 'completed', approvedAt: new Date().toISOString() } : txn;
          }));
        } catch (e) {
          // Non-fatal; leave as pending if approval failed
        }
      }

      return savedTransaction;
    } catch (error) {
      console.error('Error adding transaction:', error);
      addNotification({
        title: 'Error Adding Transaction',
        message: 'Failed to add transaction. Please try again.',
        type: 'error'
      });
      return null;
    }
  };

  const getTransactionsByType = (type) => {
    const list = Array.isArray(transactions) ? transactions : [];
    return list.filter(txn => txn && txn.type === type);
  };

  const approveTransaction = async (transactionId) => {
    try {
      // Update in API
      await api.transactionApi.approveTransaction(transactionId);

      setTransactions(prev =>
        prev.map(txn =>
          txn.id === transactionId
            ? { ...txn, status: 'completed', approvedAt: new Date().toISOString() }
            : txn
        )
      );
    } catch (error) {
      console.error('Error approving transaction:', error);
      addNotification({
        title: 'Error Approving Transaction',
        message: 'Failed to approve transaction. Please try again.',
        type: 'error'
      });
    }
  };

  const rejectTransaction = async (transactionId, reason = 'Rejected by admin') => {
    try {
      // Update in API
      await api.transactionApi.rejectTransaction(transactionId, reason);

      setTransactions(prev =>
        prev.map(txn =>
          txn.id === transactionId
            ? {
              ...txn,
              status: 'failed',
              errorMessage: reason,
              rejectedAt: new Date().toISOString()
            }
            : txn
        )
      );
    } catch (error) {
      console.error('Error rejecting transaction:', error);
      addNotification({
        title: 'Error Rejecting Transaction',
        message: 'Failed to reject transaction. Please try again.',
        type: 'error'
      });
    }
  };

  const updateTransactionStatus = async (transactionId, status, errorMessage = null) => {
    try {
      // Update in API
      await api.transactionApi.updateTransaction(transactionId, { status, errorMessage });

      setTransactions(prev =>
        prev.map(txn =>
          txn.id === transactionId
            ? {
              ...txn,
              status,
              ...(errorMessage && { errorMessage }),
              updatedAt: new Date().toISOString()
            }
            : txn
        )
      );
    } catch (error) {
      console.error('Error updating transaction status:', error);
      addNotification({
        title: 'Error Updating Transaction',
        message: 'Failed to update transaction status. Please try again.',
        type: 'error'
      });
    }
  };

  const updateTransaction = async (transactionId, updates) => {
    try {
      // Update in API
      const updatedTransaction = await api.transactionApi.updateTransaction(transactionId, updates);

      setTransactions(prev =>
        prev.map(txn =>
          txn.id === transactionId
            ? updatedTransaction
            : txn
        )
      );
    } catch (error) {
      console.error('Error updating transaction:', error);
      addNotification({
        title: 'Error Updating Transaction',
        message: 'Failed to update transaction. Please try again.',
        type: 'error'
      });
    }
  };

  // Notification normalization helper
  const isMongoObjectId = (val) => typeof val === 'string' && /^[a-fA-F0-9]{24}$/.test(val);
  const normalizeNotification = (n) => {
    if (!n) return n;
    const id = n._id || n.id || n.notificationId || `notif-${Date.now()}`;
    const status = n.status || (n.read ? 'read' : 'unread');
    const createdAt = n.createdAt || n.date || n.timestamp || n.sentAt || n.deliveredAt || new Date().toISOString();
    return { ...n, id, status, createdAt };
  };

  // Notification functions
  const addNotification = async (notification) => {
    // Deduplication: Check if a similar notification was added recently (within 5 minutes)
    const now = Date.now();
    const fiveMinutesAgo = now - (5 * 60 * 1000);

    const isDuplicate = notifications.some(existingNotif => {
      // Check if notification has same title and message
      const sameContent = existingNotif.title === notification.title &&
        existingNotif.message === notification.message;

      // Check if it was created recently (within 5 minutes)
      const createdAt = new Date(existingNotif.createdAt).getTime();
      const isRecent = createdAt > fiveMinutesAgo;

      return sameContent && isRecent;
    });

    // If duplicate found, skip adding this notification
    if (isDuplicate) {
      console.log('Skipping duplicate notification:', notification.title);
      return;
    }

    const newNotification = {
      ...notification,
      id: `notif-${Date.now()}`,
      priority: notification.priority || 'medium',
      status: 'unread',
      createdAt: new Date().toISOString(),
      readAt: null,
      archived: false,
      actions: notification.actions || [],
      pushEnabled: notification.pushEnabled !== false, // Default to true unless explicitly disabled
      pushSent: false,
      audience: notification.audience || (notification.isSystem ? 'system' : 'user')
    };

    // Always add to local state first for immediate UI update
    setNotifications(prev => [newNotification, ...prev]);

    // Only try to save to API if user is authenticated and has a valid user object
    const token = localStorage.getItem('auth_token');
    const hasUser = user && (user.id || user._id);

    if (!token || !hasUser) {
      // Not fully authenticated: store locally only
      return;
    }

    try {
      // Save to API when authenticated - add userId from current user
      const notificationWithUserId = {
        ...newNotification,
        userId: user.id || user._id
      };
      const savedNotification = await api.notificationApi.createNotification(notificationWithUserId);

      // Update with server response (which has MongoDB _id)
      setNotifications(prev =>
        prev.map(n => n.id === newNotification.id ? normalizeNotification(savedNotification) : n)
      );

      // Trigger push notification if enabled and configured
      if (newNotification.pushEnabled && pushNotificationSettings.enabled) {
        sendPushNotification(newNotification);
      }
    } catch (error) {
      // Silently fail - notification is already in local state
      // Only log if it's not a common auth error or network timeout
      if (error?.status !== 401 && error?.status !== 403 && !error?.message?.includes('timeout')) {
        console.error('Error syncing notification to server:', error);
      }
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      // Always update UI instantly
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, status: 'read', readAt: new Date().toISOString() }
            : notif
        )
      );

      const token = localStorage.getItem('auth_token');
      if (!token) return; // No token => local-only update

      // Only call API when we have a valid MongoDB id
      const apiId = isMongoObjectId(notificationId) ? notificationId : null;
      if (!apiId) return;

      await api.notificationApi.markAsRead(apiId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markNotificationAsUnread = async (notificationId) => {
    try {
      // Update UI instantly
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, status: 'unread', readAt: null }
            : notif
        )
      );

      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const apiId = isMongoObjectId(notificationId) ? notificationId : null;
      if (!apiId) return;

      await api.notificationApi.markAsUnread(apiId);
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  };

  const archiveNotification = (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId
          ? { ...notif, archived: true }
          : notif
      )
    );
  };

  const unarchiveNotification = (notificationId) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId
          ? { ...notif, archived: false }
          : notif
      )
    );
  };

  const deleteNotification = async (notificationId) => {
    try {
      // Update UI immediately
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const apiId = isMongoObjectId(notificationId) ? notificationId : null;
      if (!apiId) return;

      await api.notificationApi.deleteNotification(apiId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(prev =>
      prev.map(notif =>
        !notif.archived
          ? { ...notif, status: 'read', readAt: new Date().toISOString() }
          : notif
      )
    );
  };

  // Clear all notifications (mark read and archive)
  const clearAllNotifications = () => {
    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => ({ ...n, status: 'read', readAt: now, archived: true })));
  };

  // Delete all notifications (local flush + best-effort API bulk delete where possible)
  const deleteAllNotifications = async () => {
    try {
      // Capture current list, then clear UI immediately
      const list = Array.isArray(notifications) ? notifications : [];
      setNotifications([]);

      // If not authenticated, we’re done (local-only)
      const token = localStorage.getItem('auth_token');
      if (!token) return { success: true, deleted: 0 };

      // Best-effort bulk delete on server for notifications with MongoDB IDs
      const ids = list.map(n => n && n.id).filter(Boolean).filter(isMongoObjectId);
      if (ids.length === 0) return { success: true, deleted: 0 };

      try {
        const res = await api.notificationApi.deleteNotificationsBulk(ids);
        const deleted = typeof res?.deleted === 'number' ? res.deleted : ids.length;
        return { success: true, deleted };
      } catch (bulkError) {
        // Fallback: try per-id delete if bulk fails
        const results = await Promise.allSettled(ids.map(id => api.notificationApi.deleteNotification(id)));
        const deleted = results.filter(r => r.status === 'fulfilled').length;
        return { success: true, deleted };
      }
    } catch (err) {
      // Non-fatal: UI already cleared
      return { success: false, error: err?.message || 'Failed to delete all notifications' };
    }
  };

  // Push Notification Functions
  const updatePushNotificationSettings = (settings) => {
    setPushNotificationSettings(prev => {
      let updated = { ...prev };

      // Handle nested push settings (from PushSettingsModal)
      if (settings && settings.push) {
        updated.push = { ...updated.push, ...settings.push };
        // Sync flat properties with nested ones
        updated.fcmConfigured = updated.push.enableFCM;
        updated.apnsConfigured = updated.push.enableAPNS;
        updated.webPushConfigured = updated.push.enableWebPush;
        updated.defaultPriority = updated.push.defaultPriority;
        updated.ttl = updated.push.defaultTTL;
        updated.enableAnalytics = updated.push.enableAnalytics;
        updated.enableDeliveryTracking = updated.push.enableDeliveryReports;
        updated.fcmServerKey = updated.push.fcmServerKey;
        updated.apnsKeyId = updated.push.apnsKeyId;
        updated.apnsTeamId = updated.push.apnsTeamId;
        updated.apnsBundleId = updated.push.apnsBundleId;
        updated.apnsEnvironment = updated.push.apnsEnvironment;
        updated.vapidPublicKey = updated.push.vapidPublicKey;
        updated.vapidPrivateKey = updated.push.vapidPrivateKey;
      } else if (settings && typeof settings === 'object') {
        // Handle direct flat settings update
        updated = { ...updated, ...settings };
        // Sync nested properties with flat ones
        updated.push = { ...updated.push, ...settings };
      }

      return updated;
    });
  };

  const sendPushNotification = async (notification) => {
    if (!pushNotificationSettings.enabled) return;

    const pushRecord = {
      id: `push-${Date.now()}`,
      notificationId: notification.id,
      status: 'sending',
      sentAt: new Date().toISOString(),
      deliveredAt: null,
      deviceToken: null,
      errorMessage: null
    };

    // Determine platform and send push notification
    if (pushNotificationSettings.fcmConfigured) {
      pushRecord.platform = 'fcm';
      pushRecord.deviceToken = 'fcm-device-token'; // In real app, get from user device
      // Simulate FCM push notification
      setTimeout(() => {
        setPushNotificationHistory(prev => prev.map(record =>
          record.id === pushRecord.id
            ? { ...record, status: 'delivered', deliveredAt: new Date().toISOString() }
            : record
        ));
        setNotifications(prev => prev.map(notif =>
          notif.id === notification.id
            ? { ...notif, pushSent: true }
            : notif
        ));
      }, 1000);
    } else if (pushNotificationSettings.apnsConfigured) {
      pushRecord.platform = 'apns';
      pushRecord.deviceToken = 'apns-device-token'; // In real app, get from user device
      // Simulate APNS push notification
      setTimeout(() => {
        setPushNotificationHistory(prev => prev.map(record =>
          record.id === pushRecord.id
            ? { ...record, status: 'delivered', deliveredAt: new Date().toISOString() }
            : record
        ));
        setNotifications(prev => prev.map(notif =>
          notif.id === notification.id
            ? { ...notif, pushSent: true }
            : notif
        ));
      }, 1500);
    } else if (pushNotificationSettings.webPushConfigured) {
      pushRecord.platform = 'web';
      pushRecord.deviceToken = 'web-push-subscription'; // In real app, get from service worker
      // Simulate Web Push notification
      setTimeout(() => {
        setPushNotificationHistory(prev => prev.map(record =>
          record.id === pushRecord.id
            ? { ...record, status: 'delivered', deliveredAt: new Date().toISOString() }
            : record
        ));
        setNotifications(prev => prev.map(notif =>
          notif.id === notification.id
            ? { ...notif, pushSent: true }
            : notif
        ));
      }, 800);
    }

    setPushNotificationHistory(prev => [pushRecord, ...prev]);
  };

  const getPushNotificationStats = () => {
    const total = pushNotificationHistory.length;
    const delivered = pushNotificationHistory.filter(p => p.status === 'delivered').length;
    const failed = pushNotificationHistory.filter(p => p.status === 'failed').length;
    const pending = pushNotificationHistory.filter(p => p.status === 'sending').length;

    return { total, delivered, failed, pending, successRate: total > 0 ? (delivered / total * 100).toFixed(1) : 0 };
  };

  // Admin-controlled push notification toggle
  const togglePushNotifications = (enabled) => {
    // This function should only be called by admin components
    setPushNotificationSettings(prev => ({
      ...prev,
      enabled
    }));
  };

  // User cannot directly modify push notification settings
  const updateUserPushNotificationSettings = (settings) => {
    // Prevent users from modifying global push notification settings
    console.warn('Push notification settings are controlled by administrators');
    return false;
  };

  const updateNotificationPushPreference = (notificationId, pushEnabled) => {
    setNotifications(prev => prev.map(notif =>
      notif.id === notificationId
        ? { ...notif, pushEnabled }
        : notif
    ));
  };

  const addInvestment = async (investment) => {
    try {
      // If not authenticated, add locally and inform the user
      const token = localStorage.getItem('auth_token');
      if (!token) {
        const localInv = { ...investment, id: investment.id || `inv-${Date.now()}` };
        setUserInvestments(prev => [...prev, localInv]);
        addNotification({
          title: 'Investment Added (Local Only)',
          message: 'You are not logged in. Investment saved locally for this session.',
          type: 'warning'
        });
        return { ...localInv, local: true };
      }

      // Persist minimal required fields to API
      const payload = {
        userId: investment.userId,
        planName: investment.planName,
        amount: investment.amount,
      };
      const savedInvestment = await api.investmentApi.createInvestment(payload);
      const normalized = normalizeInvestment(savedInvestment);
      setUserInvestments(prev => [...prev, normalized]);
      return normalized;
    } catch (error) {
      // If unauthorized, fallback to local save
      if (error?.status === 401) {
        const localInv = { ...investment, id: investment.id || `inv-${Date.now()}` };
        setUserInvestments(prev => [...prev, localInv]);
        addNotification({
          title: 'Login Required',
          message: 'Could not save to server. Investment saved locally for this session.',
          type: 'warning'
        });
        return { ...localInv, local: true };
      }
      console.error('Error adding investment:', error);
      addNotification({
        title: 'Error Adding Investment',
        message: 'Failed to add investment. Please try again.',
        type: 'error'
      });
      throw error;
    }
  };

  // Function to refresh investment plans (mock for frontend-only mode)
  const refreshInvestmentPlans = async () => {
    try {
      await loadInvestmentPlans();
      return true;
    } catch (error) {
      console.error('Error refreshing investment plans:', error);
      return false;
    }
  };

  // Function to test a specific provider
  const testProvider = async (providerKey) => {
    const result = await fetchFromProvider(providerKey);
    if (result.success) {
      addNotification({
        title: 'Provider Test Successful',
        message: `${goldRateApiSettings.providers[providerKey].name} returned rate: ₹${result.rate} (per 10g)`,
        type: 'success'
      });
    } else {
      addNotification({
        title: 'Provider Test Failed',
        message: `${goldRateApiSettings.providers[providerKey].name}: ${result.error}`,
        type: 'error'
      });
    }
    return result;
  };

  // Function to get provider statistics
  const getProviderStats = () => {
    const stats = {};
    Object.keys(goldRateApiSettings.providers).forEach(providerKey => {
      const provider = goldRateApiSettings.providers[providerKey];
      stats[providerKey] = {
        name: provider.name,
        status: provider.status,
        enabled: provider.enabled,
        lastRate: provider.lastRate, // per 10g for UI
        lastUpdate: provider.lastUpdate,
        currency: provider.currency,
        priceUnit: provider.priceUnit,
        errorCount: provider.errorCount,
        lastError: provider.lastError,
        uptime: provider.lastUpdate ? 'Active' : 'Inactive'
      };
    });
    return stats;
  };

  // Function to reset provider error count
  const resetProviderErrors = (providerKey) => {
    setGoldRateApiSettings(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [providerKey]: {
          ...prev.providers[providerKey],
          errorCount: 0,
          lastError: null
        }
      }
    }));
  };

  // Currency conversion settings updater and tester
  const updateCurrencyConversionSettings = (newSettings) => {
    setGoldRateApiSettings(prev => ({
      ...prev,
      currencyConversion: {
        ...prev.currencyConversion,
        ...newSettings
      }
    }));
  };

  const testCurrencyConversion = async () => {
    try {
      const rate = await getUsdToInrRate();
      addNotification({
        title: 'Currency Conversion OK',
        message: `1 USD = ₹${rate.toFixed(2)} (via ${goldRateApiSettings.currencyConversion.provider})`,
        type: 'success'
      });
      return { success: true, rate };
    } catch (err) {
      addNotification({
        title: 'Currency Conversion Failed',
        message: err.message,
        type: 'error'
      });
      return { success: false, error: err.message };
    }
  };

  // Function to get rate history
  const getRateHistory = (limit = 50) => {
    return goldRateApiSettings.rateHistory.slice(0, limit);
  };

  // Function to clear rate history
  const clearRateHistory = () => {
    setGoldRateApiSettings(prev => ({
      ...prev,
      rateHistory: []
    }));
  };

  // Persist gold rate API settings in backend key-value store (admin-only)
  const saveGoldRateSettingsToServer = async () => {
    try {
      // Skip entirely if backend doesn't expose settings key-value API
      const okSettings = await supportsSettingsKeyStore();
      if (!okSettings) {
        return { success: false, reason: 'unsupported' };
      }

      const adminStatus = await ensureAdminAuth();
      if (!adminStatus?.ok) {
        return {
          success: false,
          reason: adminStatus?.tokenPresent ? 'not_admin' : 'unauthenticated',
        };
      }

      const res = await api.settingsApi.setKeyValue('goldRateApiSettings', goldRateApiSettings);
      return { success: true, data: res };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to save gold rate settings' };
    }
  };

  // Load gold rate API settings from backend (admin-only). Merges over current defaults.
  const loadGoldRateSettingsFromServer = async () => {
    try {
      // Skip if backend does not support settings key-value API
      const okSettings = await supportsSettingsKeyStore();
      if (!okSettings) {
        return { success: false, reason: 'unsupported' };
      }

      const adminStatus = await ensureAdminAuth();
      if (!adminStatus?.ok) {
        return {
          success: false,
          reason: adminStatus?.tokenPresent ? 'not_admin' : 'unauthenticated',
        };
      }

      const res = await api.settingsApi.getKeyValue('goldRateApiSettings');
      const serverSettings = res?.value;
      if (!serverSettings || typeof serverSettings !== 'object') {
        return { success: false, reason: 'not_found' };
      }

      setGoldRateApiSettings(prev => ({
        ...prev,
        ...serverSettings,
        providers: {
          ...prev.providers,
          ...(serverSettings.providers || {}),
        },
        currencyConversion: {
          ...prev.currencyConversion,
          ...(serverSettings.currencyConversion || {}),
        },
      }));

      return { success: true, data: serverSettings };
    } catch (error) {
      if (error?.status === 404) {
        return { success: false, reason: 'not_found' };
      }
      return { success: false, error: error?.message || 'Failed to load gold rate settings' };
    }
  };

  // Function to update user KYC status
  const updateUserKYCStatus = async (userId, newStatus) => {
    try {
      // Update in API
      await api.userApi.updateUser(userId, { kycStatus: newStatus });

      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? {
              ...user,
              kycStatus: newStatus,
              trustScore: calculateTrustScore({ ...user, kycStatus: newStatus }),
              riskProfile: getRiskProfile(calculateTrustScore({ ...user, kycStatus: newStatus }))
            }
            : user
        )
      );

      // Also update the corresponding KYC record
      setKycRecords(prevRecords =>
        prevRecords.map(record =>
          record.userId === userId
            ? { ...record, status: newStatus }
            : record
        )
      );
    } catch (error) {
      console.error('Error updating user KYC status:', error);
      addNotification({
        title: 'Error Updating KYC Status',
        message: 'Failed to update KYC status. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to update user account status
  const updateUserAccountStatus = async (userId, newStatus) => {
    try {
      // Update in API
      await api.userApi.updateUser(userId, { status: newStatus });

      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? {
              ...user,
              status: newStatus,
              trustScore: calculateTrustScore({ ...user, status: newStatus }),
              riskProfile: getRiskProfile(calculateTrustScore({ ...user, status: newStatus }))
            }
            : user
        )
      );
    } catch (error) {
      console.error('Error updating user account status:', error);
      addNotification({
        title: 'Error Updating Account Status',
        message: 'Failed to update account status. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to update user investment data
  const updateUserInvestmentData = async (userId, investmentData) => {
    try {
      // Update in API
      await api.userApi.updateUser(userId, investmentData);

      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? {
              ...user,
              totalInvestment: investmentData.totalInvestment,
              currentROI: investmentData.currentROI,
              trustScore: calculateTrustScore({
                ...user,
                totalInvestment: investmentData.totalInvestment,
                currentROI: investmentData.currentROI
              }),
              riskProfile: getRiskProfile(calculateTrustScore({
                ...user,
                totalInvestment: investmentData.totalInvestment,
                currentROI: investmentData.currentROI
              }))
            }
            : user
        )
      );
    } catch (error) {
      console.error('Error updating user investment data:', error);
      addNotification({
        title: 'Error Updating Investment Data',
        message: 'Failed to update investment data. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to add a new user
  const addUser = async (newUser) => {
    try {
      const userWithDefaults = {
        id: `user-${Date.now()}`,
        referredUsers: [],
        referralBonuses: 0,
        totalReferralEarnings: 0,
        trustScore: 50,
        riskProfile: 'Medium Risk',
        fraudAlerts: [],
        ...newUser
      };

      // Save to API
      const savedUser = await api.userApi.createUser(userWithDefaults);

      const normalized = {
        ...savedUser,
        id: savedUser.id || savedUser._id || savedUser.userId || userWithDefaults.id,
        name: savedUser.name || savedUser.fullName || userWithDefaults.name,
        email: savedUser.email || userWithDefaults.email || '',
        phone: savedUser.phone || savedUser.mobile || userWithDefaults.phone || '',
        joinDate: savedUser.joinDate || (savedUser.createdAt ? new Date(savedUser.createdAt).toISOString().split('T')[0] : userWithDefaults.joinDate),
        kycStatus: savedUser.kycStatus || (savedUser.kyc && savedUser.kyc.status) || userWithDefaults.kycStatus || 'pending',
        status: savedUser.status || savedUser.accountStatus || userWithDefaults.status || 'active',
        totalInvestment: Number(savedUser.totalInvestment ?? userWithDefaults.totalInvestment ?? 0),
        currentROI: Number(savedUser.currentROI ?? userWithDefaults.currentROI ?? 0),
        lastLogin: savedUser.lastLogin || 'Never',
        fraudAlerts: Array.isArray(savedUser.fraudAlerts) ? savedUser.fraudAlerts : []
      };
      setUsers(prevUsers => [...prevUsers, normalized]);
    } catch (error) {
      console.error('Error adding user:', error);
      addNotification({
        title: 'Error Adding User',
        message: 'Failed to add user. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to update user profile data
  const updateUserProfile = async (userId, profileData) => {
    try {
      // Update in API
      const updatedUser = await api.userApi.updateUser(userId, profileData);

      const normalized = {
        ...updatedUser,
        id: updatedUser.id || updatedUser._id || userId,
      };
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId
            ? { ...user, ...normalized }
            : user
        )
      );
    } catch (error) {
      console.error('Error updating user profile:', error);
      addNotification({
        title: 'Error Updating Profile',
        message: 'Failed to update profile. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to delete a user account
  const deleteUser = async (userId) => {
    try {
      // Delete from API
      await api.userApi.deleteUser(userId);

      // Update local state
      setUsers(prev => prev.filter(u => u.id !== userId));

      // Optional notification
      addNotification({
        title: 'User Deleted',
        message: 'The user account has been removed successfully.',
        type: 'info'
      });
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      addNotification({
        title: 'Error Deleting User',
        message: 'Failed to delete user. Please try again.',
        type: 'error'
      });
      return false;
    }
  };

  // Function to calculate trust score based on user activity (copied from AdminUsers component)
  const calculateTrustScore = (user) => {
    let score = 50; // Base score

    // KYC Status (30 points)
    if (user.kycStatus === 'approved') score += 30;
    else if (user.kycStatus === 'pending') score += 10;
    else if (user.kycStatus === 'rejected') score -= 20;

    // Account Status (20 points)
    if (user.status === 'active') score += 20;
    else if (user.status === 'suspended') score -= 15;

    // Investment Amount (20 points)
    if (user.totalInvestment >= 100000) score += 20;
    else if (user.totalInvestment >= 50000) score += 15;
    else if (user.totalInvestment >= 25000) score += 10;
    else if (user.totalInvestment >= 10000) score += 5;

    // Account Age (15 points)
    const joinDate = new Date(user.joinDate);
    const now = new Date();
    const accountAgeDays = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
    if (accountAgeDays > 180) score += 15; // 6+ months
    else if (accountAgeDays > 90) score += 10; // 3+ months
    else if (accountAgeDays > 30) score += 5; // 1+ month

    // Recent Activity (15 points)
    if (user.lastLogin !== 'Never') {
      const lastLogin = new Date(user.lastLogin);
      const daysSinceLogin = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
      if (daysSinceLogin <= 7) score += 15; // Very active
      else if (daysSinceLogin <= 30) score += 10; // Active
      else if (daysSinceLogin <= 90) score += 5; // Moderately active
    }

    // ROI Performance (bonus points)
    const roiPercentage = user.totalInvestment > 0 ? (user.currentROI / user.totalInvestment) * 100 : 0;
    if (roiPercentage >= 5) score += 5; // Good ROI performance

    // Cap score between 0-100
    score = Math.max(0, Math.min(100, score));

    return score;
  };

  // Function to determine risk profile based on trust score (copied from AdminUsers component)
  const getRiskProfile = (trustScore) => {
    if (trustScore >= 80) return 'Low Risk';
    if (trustScore >= 60) return 'Medium Risk';
    return 'High Risk';
  };

  // Function to detect fraudulent activities (copied from AdminUsers component)
  const detectFraudAlerts = (user) => {
    const alerts = [];
    const now = new Date();
    const joinDate = new Date(user.joinDate);

    // Check for abnormal ROI (ROI > 10% for accounts less than 3 months old)
    const accountAgeDays = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
    const roiPercentage = user.totalInvestment > 0 ? (user.currentROI / user.totalInvestment) * 100 : 0;

    if (accountAgeDays < 90 && roiPercentage > 10) {
      alerts.push({
        type: 'abnormal_roi',
        severity: 'high',
        message: `Abnormal ROI: ${roiPercentage.toFixed(1)}% in ${accountAgeDays} days`,
        description: 'Unusually high returns for new account'
      });
    }

    // Check for large investment with low account age
    if (accountAgeDays < 30 && user.totalInvestment > 50000) {
      alerts.push({
        type: 'large_investment_new_account',
        severity: 'medium',
        message: 'Large investment in new account',
        description: `₹${user.totalInvestment.toLocaleString()} invested within ${accountAgeDays} days of joining`
      });
    }

    // Check for rejected KYC but active account
    if (user.kycStatus === 'rejected' && user.status === 'active') {
      alerts.push({
        type: 'rejected_kyc_active',
        severity: 'high',
        message: 'Active account with rejected KYC',
        description: 'User has rejected KYC verification but account remains active'
      });
    }

    // Check for suspended account with high investment
    if (user.status === 'suspended' && user.totalInvestment > 100000) {
      alerts.push({
        type: 'suspended_high_investment',
        severity: 'medium',
        message: 'Suspended account with high investment',
        description: 'Account suspended but has significant investment amount'
      });
    }

    // Check for very high ROI (>50%)
    if (roiPercentage > 50) {
      alerts.push({
        type: 'extremely_high_roi',
        severity: 'critical',
        message: `Extremely high ROI: ${roiPercentage.toFixed(1)}%`,
        description: 'ROI percentage exceeds normal thresholds'
      });
    }

    // Check for new account with immediate high activity
    if (accountAgeDays < 7 && (user.totalInvestment > 10000 || user.currentROI > 1000)) {
      alerts.push({
        type: 'rapid_activity',
        severity: 'medium',
        message: 'Rapid activity in new account',
        description: 'High investment/ROI activity within first week'
      });
    }

    return alerts;
  };

  const addUserFraudAlerts = (userId, alerts) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
              ...user,
              fraudAlerts: [...(user.fraudAlerts || []), ...alerts]
            }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error adding fraud alerts:', error);
      throw error;
    }
  };

  const addUserFraudAlert = (userId, alert) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
              ...user,
              fraudAlerts: [...(user.fraudAlerts || []), alert]
            }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error adding fraud alert:', error);
      throw error;
    }
  };

  const clearUserFraudAlerts = (userId) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? { ...user, fraudAlerts: [] }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error clearing fraud alerts:', error);
      throw error;
    }
  };

  const removeUserFraudAlert = (userId, alertIndex) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
              ...user,
              fraudAlerts: user.fraudAlerts.filter((_, index) => index !== alertIndex)
            }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error removing fraud alert:', error);
      throw error;
    }
  };

  const updateUserFraudAlert = (userId, alertIndex, updates) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
              ...user,
              fraudAlerts: user.fraudAlerts.map((alert, index) =>
                index === alertIndex ? { ...alert, ...updates } : alert
              )
            }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error updating fraud alert:', error);
      throw error;
    }
  };

  const resolveUserFraudAlert = (userId, alertIndex) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
              ...user,
              fraudAlerts: user.fraudAlerts.map((alert, index) =>
                index === alertIndex ? { ...alert, resolved: true, resolvedAt: new Date().toISOString() } : alert
              )
            }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error resolving fraud alert:', error);
      throw error;
    }
  };

  const resolveAllUserFraudAlerts = (userId) => {
    try {
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? {
              ...user,
              fraudAlerts: user.fraudAlerts.map(alert => ({ ...alert, resolved: true, resolvedAt: new Date().toISOString() }))
            }
            : user
        )
      );

      return { success: true };
    } catch (error) {
      console.error('Error resolving all fraud alerts:', error);
      throw error;
    }
  };

  const getUnresolvedUserFraudAlerts = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.fraudAlerts.filter(alert => !alert.resolved) : [];
  };

  const getUserFraudAlertsBySeverity = (userId, severity) => {
    const user = users.find(u => u.id === userId);
    return user ? user.fraudAlerts.filter(alert => alert.severity === severity) : [];
  };

  const getUserFraudAlertsByType = (userId, type) => {
    const user = users.find(u => u.id === userId);
    return user ? user.fraudAlerts.filter(alert => alert.type === type) : [];
  };

  const getAllFraudAlerts = () => {
    const list = Array.isArray(users) ? users : [];
    return list.flatMap(user =>
      (Array.isArray(user.fraudAlerts) ? user.fraudAlerts : []).map(alert => ({ ...alert, userId: user.id, userName: user.name }))
    );
  };

  const getAllUnresolvedFraudAlerts = () => {
    const list = Array.isArray(users) ? users : [];
    return list.flatMap(user =>
      (Array.isArray(user.fraudAlerts) ? user.fraudAlerts : [])
        .filter(alert => !alert.resolved)
        .map(alert => ({ ...alert, userId: user.id, userName: user.name }))
    );
  };

  const getAllFraudAlertsBySeverity = (severity) => {
    const list = Array.isArray(users) ? users : [];
    return list.flatMap(user =>
      (Array.isArray(user.fraudAlerts) ? user.fraudAlerts : [])
        .filter(alert => alert.severity === severity)
        .map(alert => ({ ...alert, userId: user.id, userName: user.name }))
    );
  };

  const getAllFraudAlertsByType = (type) => {
    const list = Array.isArray(users) ? users : [];
    return list.flatMap(user =>
      (Array.isArray(user.fraudAlerts) ? user.fraudAlerts : [])
        .filter(alert => alert.type === type)
        .map(alert => ({ ...alert, userId: user.id, userName: user.name }))
    );
  };

  const getFraudAlertStats = () => {
    const allAlerts = Array.isArray(getAllFraudAlerts()) ? getAllFraudAlerts() : [];
    const unresolvedAlerts = Array.isArray(getAllUnresolvedFraudAlerts()) ? getAllUnresolvedFraudAlerts() : [];

    return {
      total: allAlerts.length,
      unresolved: unresolvedAlerts.length,
      critical: allAlerts.filter(alert => alert.severity === 'critical').length,
      high: allAlerts.filter(alert => alert.severity === 'high').length,
      medium: allAlerts.filter(alert => alert.severity === 'medium').length,
      low: allAlerts.filter(alert => alert.severity === 'low').length
    };
  };

  const generateFraudReport = (format = 'json') => {
    const allAlerts = getAllFraudAlerts();

    if (format === 'csv') {
      const csvHeaders = ['User ID', 'User Name', 'User Email', 'Alert Type', 'Severity', 'Message', 'Description', 'Created At', 'Resolved', 'Resolved At'];
      const csvRows = allAlerts.map(alert => [
        alert.userId,
        alert.userName,
        alert.userEmail || '',
        alert.type,
        alert.severity,
        `"${alert.message}"`,
        `"${alert.description}"`,
        alert.createdAt || new Date().toISOString(),
        alert.resolved ? 'Yes' : 'No',
        alert.resolvedAt || ''
      ]);

      return [csvHeaders.join(','), ...csvRows.map(row => row.join(','))].join('\n');
    }

    return JSON.stringify(allAlerts, null, 2);
  };

  const exportFraudAlerts = (format = 'json') => {
    return generateFraudReport(format);
  };

  const importFraudAlerts = (alertsData) => {
    try {
      const alerts = typeof alertsData === 'string' ? JSON.parse(alertsData) : alertsData;

      alerts.forEach(alert => {
        if (alert.userId) {
          addUserFraudAlert(alert.userId, alert);
        }
      });

      return { success: true, importedCount: alerts.length };
    } catch (error) {
      console.error('Error importing fraud alerts:', error);
      return { success: false, error: error.message };
    }
  };

  // Save gold rate to backend (if admin and auto-save enabled)
  const saveGoldRateToBackend = useCallback(async (rate) => {
    try {
      // NOTE: POST /gold-rates is deprecated (returns 410 Gone)
      // The new gold pricing module doesn't require manual rate publishing
      // Rates are automatically saved when fetched via GET /gold-price
      console.log('ℹ️ Rate auto-saved by backend when fetched');
      return { success: true, message: 'Rate managed by new gold pricing module' };

      // Old code (deprecated):
      // const result = await api.goldRateApi.updateRate({ rate, source: 'admin' });
      // return result;
    } catch (error) {
      console.error('Failed to save gold rate:', error);
      return { success: false, error: error.message };
    }
  }, []);

  const searchFraudAlerts = (query) => {
    const allAlerts = getAllFraudAlerts();
    const lowerQuery = query.toLowerCase();

    return allAlerts.filter(alert =>
      alert.type.toLowerCase().includes(lowerQuery) ||
      alert.message.toLowerCase().includes(lowerQuery) ||
      alert.description.toLowerCase().includes(lowerQuery) ||
      alert.userName.toLowerCase().includes(lowerQuery)
    );
  };

  const filterFraudAlertsByDate = (startDate, endDate) => {
    const allAlerts = getAllFraudAlerts();

    return allAlerts.filter(alert => {
      const alertDate = new Date(alert.createdAt || alert.timestamp || new Date());
      return alertDate >= new Date(startDate) && alertDate <= new Date(endDate);
    });
  };

  const getFraudTrendData = (days = 30) => {
    const allAlerts = getAllFraudAlerts();
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const trendData = [];
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + i);

      const nextDate = new Date(currentDate);
      nextDate.setDate(nextDate.getDate() + 1);

      const alertsForDay = allAlerts.filter(alert => {
        const alertDate = new Date(alert.createdAt || alert.timestamp || new Date());
        return alertDate >= currentDate && alertDate < nextDate;
      });

      trendData.push({
        date: currentDate.toISOString().split('T')[0],
        count: alertsForDay.length,
        critical: alertsForDay.filter(alert => alert.severity === 'critical').length,
        high: alertsForDay.filter(alert => alert.severity === 'high').length,
        medium: alertsForDay.filter(alert => alert.severity === 'medium').length,
        resolved: alertsForDay.filter(alert => alert.resolved).length
      });
    }

    return trendData;
  };

  const getUserRiskScore = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return 0;

    let score = user.trustScore || 50;

    // Adjust score based on fraud alerts
    user.fraudAlerts.forEach(alert => {
      if (!alert.resolved) {
        switch (alert.severity) {
          case 'critical':
            score -= 20;
            break;
          case 'high':
            score -= 15;
            break;
          case 'medium':
            score -= 10;
            break;
          case 'low':
            score -= 5;
            break;
        }
      }
    });

    // Cap score between 0-100
    return Math.max(0, Math.min(100, score));
  };

  const getHighRiskUsers = (threshold = 30) => {
    return users.filter(user => getUserRiskScore(user.id) <= threshold);
  };

  const addCustomFraudAlert = (userId, alertData) => {
    const customAlert = {
      id: `alert-${Date.now()}`,
      type: alertData.type || 'custom',
      severity: alertData.severity || 'medium',
      message: alertData.message || 'Custom fraud alert',
      description: alertData.description || '',
      createdAt: new Date().toISOString(),
      resolved: false,
      ...alertData
    };

    addUserFraudAlert(userId, customAlert);

    // Also add a notification
    addNotification({
      title: 'New Fraud Alert',
      message: `Fraud alert detected for user ${users.find(u => u.id === userId)?.name || userId}: ${customAlert.message}`,
      type: 'warning',
      priority: 'high'
    });

    return customAlert;
  };

  const bulkResolveFraudAlerts = (alertIds) => {
    const list = Array.isArray(users) ? users : [];
    alertIds.forEach(alertId => {
      // Find the user and alert index
      for (const user of list) {
        const alerts = Array.isArray(user.fraudAlerts) ? user.fraudAlerts : [];
        const alertIndex = alerts.findIndex(alert => alert.id === alertId);
        if (alertIndex !== -1) {
          resolveUserFraudAlert(user.id, alertIndex);
          break;
        }
      }
    });
  };

  const bulkDeleteFraudAlerts = (alertIds) => {
    const list = Array.isArray(users) ? users : [];
    alertIds.forEach(alertId => {
      // Find the user and alert index
      for (const user of list) {
        const alerts = Array.isArray(user.fraudAlerts) ? user.fraudAlerts : [];
        const alertIndex = alerts.findIndex(alert => alert.id === alertId);
        if (alertIndex !== -1) {
          removeUserFraudAlert(user.id, alertIndex);
          break;
        }
      }
    });
  };

  const getUserFraudAlertHistory = (userId) => {
    const user = (Array.isArray(users) ? users : []).find(u => u.id === userId);
    return user ? (Array.isArray(user.fraudAlerts) ? user.fraudAlerts : []) : [];
  };

  const getRecentFraudAlerts = (count = 10) => {
    const allAlerts = getAllFraudAlerts();
    return allAlerts
      .sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp))
      .slice(0, count);
  };

  const getFraudAlertsByUser = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.fraudAlerts.map(alert => ({ ...alert, userId: user.id, userName: user.name })) : [];
  };

  const updateFraudAlertSettings = (newSettings) => {
    // This would typically update state, but for now we'll just log
    // Settings update logic would go here
  };

  const getFraudAlertSettings = () => {
    // Return default settings for now
    return {
      enableRealTimeMonitoring: true,
      enableEmailNotifications: true,
      enablePushNotifications: true,
      autoResolveDays: 30,
      severityThreshold: 'medium',
      enableFraudScoring: true
    };
  };

  const resetFraudAlertSystem = () => {
    setUsers(prevUsers =>
      prevUsers.map(user => ({ ...user, fraudAlerts: [] }))
    );
  };

  const simulateFraudDetection = () => {
    // Randomly add fraud alerts to some users for demo purposes
    users.forEach(user => {
      if (Math.random() < 0.3) { // 30% chance
        const fraudTypes = [
          'abnormal_roi',
          'large_investment_new_account',
          'rejected_kyc_active',
          'suspended_high_investment',
          'extremely_high_roi',
          'rapid_activity'
        ];

        const severities = ['low', 'medium', 'high', 'critical'];

        const randomType = fraudTypes[Math.floor(Math.random() * fraudTypes.length)];
        const randomSeverity = severities[Math.floor(Math.random() * severities.length)];

        const alert = {
          type: randomType,
          severity: randomSeverity,
          message: `Simulated ${randomType.replace('_', ' ')} alert`,
          description: `This is a simulated fraud alert for demonstration purposes`,
          createdAt: new Date().toISOString(),
          resolved: false
        };

        addUserFraudAlert(user.id, alert);
      }
    });
  };

  // Function to add investment with proper user data update
  const addInvestmentEnhanced = async (investment = {}) => {
    try {
      // Derive missing fields from authenticated user/context
      const authedUserId = (user?.id || user?._id || '').toString();
      const investorName = investment.investorName || user?.name || user?.fullName || '';
      const investorEmail = investment.investorEmail || user?.email || '';

      // Basic validation: must have a plan name and amount
      if (!investment.planName || investment.amount === undefined || investment.amount === null) {
        addNotification({
          title: 'Investment Details Required',
          message: 'Plan and amount are required to create an investment.',
          type: 'error'
        });
        return null;
      }

      // Ensure numeric fields are properly formatted
      const amountNum = Number(investment.amount) || 0;
      const monthlyROINum = Number(investment.monthlyROI || 0);
      const roiPaidNum = Number(investment.roiPaid || 0);
      const totalMonthsNum = parseInt(investment.totalMonths || 0);
      const completedMonthsNum = parseInt(investment.completedMonths || 0);

      // Persist minimal required fields to API (backend expects userId, planId or planName, amount)
      const payload = {
        userId: investment.userId || authedUserId,
        planName: investment.planName,
        amount: amountNum,
      };

      // Include planId if available (preferred by backend)
      if (investment.planId) {
        payload.planId = investment.planId;
      }

      // Include durationMonths if specified
      if (investment.totalMonths || investment.durationMonths) {
        payload.durationMonths = investment.totalMonths || investment.durationMonths;
      }

      // Include startDate if specified
      if (investment.startDate) {
        payload.startDate = investment.startDate;
      }

      const saved = await api.investmentApi.createInvestment(payload);
      const normalized = normalizeInvestment(saved);

      // Add to user investments
      setUserInvestments(prev => [...prev, normalized]);

      // Update user's aggregate stats if we know the user
      const targetUserId = payload.userId;
      if (targetUserId) {
        const userIndex = users.findIndex(u => u.id === targetUserId);
        if (userIndex !== -1) {
          const updatedUsers = [...users];
          const u = updatedUsers[userIndex];
          updatedUsers[userIndex] = {
            ...u,
            totalInvestment: (u.totalInvestment || 0) + amountNum,
            currentROI: (u.currentROI || 0) + roiPaidNum,
          };
          setUsers(updatedUsers);
        }
      }

      // Friendly toast/notification
      addNotification({
        title: 'Investment Created',
        message: `${investorName || 'Your'} investment in ${investment.planName} has been created successfully.`,
        type: 'success',
        emailEnabled: true // Send email for investment creation
      });

      return normalized;
    } catch (error) {
      console.error('Error adding enhanced investment:', error);
      addNotification({
        title: 'Error Adding Investment',
        message: 'Failed to add investment. Please try again.',
        type: 'error'
      });
      return null;
    }
  };

  // Function to update investment with proper user data update and validation
  const updateInvestment = async (investmentId, updates) => {
    try {
      // Validate updates
      const validatedUpdates = {};
      Object.keys(updates).forEach(key => {
        if (key === 'amount' || key === 'roiPaid' || key === 'monthlyROI') {
          validatedUpdates[key] = parseFloat(updates[key]) || 0;
        } else if (key === 'totalMonths' || key === 'completedMonths') {
          validatedUpdates[key] = parseInt(updates[key]) || 0;
        } else {
          validatedUpdates[key] = updates[key];
        }
      });

      // Update in API
      const updatedInvestment = await api.investmentApi.updateInvestment(investmentId, validatedUpdates);

      setUserInvestments(prev =>
        prev.map(inv =>
          inv.id === investmentId
            ? updatedInvestment
            : inv
        )
      );

      // If status changed to completed, update user data
      if (updates.status === 'completed') {
        const investment = userInvestments.find(inv => inv.id === investmentId);
        if (investment && investment.userId) {
          const userIndex = users.findIndex(u => u.id === investment.userId);
          if (userIndex !== -1) {
            const updatedUsers = [...users];
            const user = updatedUsers[userIndex];
            updatedUsers[userIndex] = {
              ...user,
              totalInvestment: Math.max(0, (user.totalInvestment || 0) - (investment.amount || 0))
            };
            setUsers(updatedUsers);
          }
        }
      }
    } catch (error) {
      console.error('Error updating investment:', error);
      addNotification({
        title: 'Error Updating Investment',
        message: 'Failed to update investment. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to delete investment with proper user data update
  const deleteInvestment = async (investmentId) => {
    try {
      // Find investment in either userInvestments or allInvestments
      const investment = userInvestments.find(inv => inv.id === investmentId) ||
        allInvestments.find(inv => inv.id === investmentId);

      // Try to delete from API
      try {
        await api.investmentApi.deleteInvestment(investmentId);
      } catch (apiError) {
        // If 404, investment doesn't exist in backend - still remove from local state
        const is404 = apiError?.status === 404 ||
          apiError?.response?.status === 404 ||
          apiError?.message?.includes('not found');
        if (!is404) {
          throw apiError; // Re-throw if not a 404
        }
        console.warn('Investment not found in backend, removing from local state only');
      }

      // Remove from userInvestments
      setUserInvestments(prev => prev.filter(inv => inv.id !== investmentId));

      // Remove from allInvestments (for admin panel)
      setAllInvestments(prev => {
        const updated = prev.filter(inv => inv.id !== investmentId);
        // Update localStorage for allInvestments
        try {
          localStorage.setItem('all_investments', JSON.stringify(updated));
        } catch (e) {
          console.error('Failed to update localStorage:', e);
        }
        return updated;
      });

      // Also update userInvestments in localStorage
      try {
        const storedUserInvestments = localStorage.getItem('user_investments');
        if (storedUserInvestments) {
          const parsed = JSON.parse(storedUserInvestments);
          const updated = parsed.filter(inv => inv.id !== investmentId && inv._id !== investmentId);
          localStorage.setItem('user_investments', JSON.stringify(updated));
        }
      } catch (e) {
        console.error('Failed to update user_investments in localStorage:', e);
      }

      // Update user's investment data
      if (investment && investment.userId) {
        const userIndex = users.findIndex(u => u.id === investment.userId);
        if (userIndex !== -1) {
          const updatedUsers = [...users];
          const user = updatedUsers[userIndex];
          updatedUsers[userIndex] = {
            ...user,
            totalInvestment: Math.max(0, (user.totalInvestment || 0) - (investment.amount || 0)),
            currentROI: Math.max(0, (user.currentROI || 0) - (investment.roiPaid || 0))
          };
          setUsers(updatedUsers);
        }
      }

      addNotification({
        title: 'Investment Deleted',
        message: 'Investment deleted successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting investment:', error);
      addNotification({
        title: 'Error Deleting Investment',
        message: 'Failed to delete investment. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to process ROI payment
  const processROIPayment = async (investmentId) => {
    try {
      // Check in both userInvestments and allInvestments (for admin panel)
      const investment = userInvestments.find(inv => inv.id === investmentId) ||
        allInvestments.find(inv => inv.id === investmentId);
      if (!investment) {
        addNotification({
          title: 'Error Processing ROI',
          message: 'Investment not found.',
          type: 'error'
        });
        return;
      }

      // Process ROI payment in API
      const updatedInvestment = await api.investmentApi.processROIPayment(investmentId);

      // Update userInvestments if investment is in it
      setUserInvestments(prev =>
        prev.map(inv =>
          inv.id === investmentId
            ? updatedInvestment
            : inv
        )
      );

      // Update allInvestments if investment is in it (for admin panel)
      setAllInvestments(prev =>
        prev.map(inv =>
          inv.id === investmentId
            ? updatedInvestment
            : inv
        )
      );

      // Update user's ROI data
      if (investment.userId) {
        const userIndex = users.findIndex(u => u.id === investment.userId);
        if (userIndex !== -1) {
          const updatedUsers = [...users];
          const user = updatedUsers[userIndex];
          updatedUsers[userIndex] = {
            ...user,
            currentROI: (user.currentROI || 0) + (investment.monthlyROI || 0)
          };
          setUsers(updatedUsers);
        }
      }

      addNotification({
        title: 'ROI Payment Processed',
        message: `ROI payment of ₹${investment.monthlyROI.toLocaleString()} processed successfully!`,
        type: 'success',
        emailEnabled: true // Send email for ROI payments
      });

      return updatedInvestment;
    } catch (error) {
      console.error('Error processing ROI payment:', error);
      addNotification({
        title: 'Error Processing ROI',
        message: 'Failed to process ROI payment. Please try again.',
        type: 'error'
      });
    }
  };

  // Function to add a new investment plan
  const addInvestmentPlan = async (plan) => {
    try {
      // Try to ensure token (attempt refresh if missing), but do not block on client-side
      if (!localStorage.getItem('auth_token') && localStorage.getItem('refresh_token')) {
        try { await authService.refreshToken(); } catch { }
      }

      // Map UI shape -> backend DTO
      const payload = {
        name: plan.title?.trim() || '',
        description: plan.description?.trim() || '',
        minInvestment: Number(plan.minAmount) || 0,
        maxInvestment: Number(plan.maxAmount) || 0,
        roiPercentage: Number(plan.roiPercentMonthly) || 0,
        durationMonths: parseInt(plan.durationMonths) || 0,
        isActive: typeof plan.active === 'boolean' ? plan.active : true,
        // Optional fields the backend may ignore
        tags: Array.isArray(plan.tags) ? plan.tags : []
      };

      const saved = await api.planApi.createPlan(payload);

      // Normalize backend -> UI
      const normalized = {
        id: saved._id || saved.id || `plan-${Date.now()}`,
        title: saved.name || payload.name,
        description: saved.description || payload.description,
        minAmount: typeof saved.minInvestment === 'number' ? saved.minInvestment : payload.minInvestment,
        maxAmount: typeof saved.maxInvestment === 'number' ? saved.maxInvestment : payload.maxInvestment,
        roiPercentMonthly: typeof saved.roiPercentage === 'number' ? saved.roiPercentage : payload.roiPercentage,
        roiPercentAnnual: (typeof saved.roiPercentage === 'number' ? saved.roiPercentage : payload.roiPercentage) * 12,
        durationMonths: saved.durationMonths ?? payload.durationMonths,
        active: typeof saved.isActive === 'boolean' ? saved.isActive : payload.isActive,
        displayOrder: plan.displayOrder ?? saved.displayOrder ?? 0,
        color: plan.color || 'bg-gradient-to-r from-blue-400 to-blue-600',
        tags: Array.isArray(saved.tags) ? saved.tags : (Array.isArray(plan.tags) ? plan.tags : []),
        features: Array.isArray(saved.features) ? saved.features : (Array.isArray(plan.features) ? plan.features : [])
      };

      setInvestmentPlans(prev => [...prev, normalized]);
      // Schedule a background refresh
      setTimeout(() => { loadInvestmentPlans().catch(() => { }); }, 800);
      return normalized;
    } catch (error) {
      console.error('Error adding investment plan:', error);
      const message = error?.status === 401 ? 'Authentication required or insufficient permissions.' : (error?.status === 403 ? 'Access denied. Admin permissions required.' : 'Failed to add investment plan. Please try again.');
      addNotification({
        title: 'Error Adding Investment Plan',
        message,
        type: 'error'
      });
      return null;
    }
  };

  // Function to update an investment plan
  const updateInvestmentPlan = async (planId, updates) => {
    try {
      // Try to ensure token
      if (!localStorage.getItem('auth_token') && localStorage.getItem('refresh_token')) {
        try { await authService.refreshToken(); } catch { }
      }

      // Map UI -> backend DTO (only include updatable fields)
      const payload = {
        ...(updates.title !== undefined ? { name: updates.title } : {}),
        ...(updates.description !== undefined ? { description: updates.description } : {}),
        ...(updates.minAmount !== undefined ? { minInvestment: Number(updates.minAmount) || 0 } : {}),
        ...(updates.maxAmount !== undefined ? { maxInvestment: Number(updates.maxAmount) || 0 } : {}),
        ...(updates.roiPercentMonthly !== undefined ? { roiPercentage: Number(updates.roiPercentMonthly) || 0 } : {}),
        ...(updates.durationMonths !== undefined ? { durationMonths: parseInt(updates.durationMonths) || 0 } : {}),
        ...(updates.active !== undefined ? { isActive: !!updates.active } : {}),
        ...(updates.tags !== undefined ? { tags: Array.isArray(updates.tags) ? updates.tags : [] } : {}),
        ...(updates.features !== undefined ? { features: Array.isArray(updates.features) ? updates.features : [] } : {}),
        ...(updates.displayOrder !== undefined ? { displayOrder: parseInt(updates.displayOrder) || 0 } : {})
      };

      const saved = await api.planApi.updatePlan(planId, payload);

      const normalized = (p => ({
        id: p._id || p.id || planId,
        title: p.name ?? updates.title,
        description: p.description ?? updates.description,
        minAmount: (p.minInvestment !== undefined ? p.minInvestment : updates.minAmount),
        maxAmount: (p.maxInvestment !== undefined ? p.maxInvestment : updates.maxAmount),
        roiPercentMonthly: (p.roiPercentage !== undefined ? p.roiPercentage : updates.roiPercentMonthly),
        roiPercentAnnual: ((p.roiPercentage !== undefined ? p.roiPercentage : updates.roiPercentMonthly) || 0) * 12,
        durationMonths: (p.durationMonths !== undefined ? p.durationMonths : updates.durationMonths),
        active: (typeof p.isActive === 'boolean' ? p.isActive : updates.active),
        displayOrder: (p.displayOrder !== undefined ? p.displayOrder : updates.displayOrder) ?? 0,
        color: p.color || updates.color || 'bg-gradient-to-r from-blue-400 to-blue-600',
        tags: Array.isArray(p.tags) ? p.tags : (Array.isArray(updates.tags) ? updates.tags : []),
        features: Array.isArray(p.features) ? p.features : (Array.isArray(updates.features) ? updates.features : [])
      }))(saved || {});

      setInvestmentPlans(prev =>
        prev.map(plan =>
          plan.id === planId
            ? { ...plan, ...normalized }
            : plan
        )
      );
      // Schedule a background refresh to reconcile with server
      setTimeout(() => { loadInvestmentPlans().catch(() => { }); }, 800);
      return normalized;
    } catch (error) {
      console.error('Error updating investment plan:', error);
      const message = error?.status === 401 ? 'Authentication required or insufficient permissions.' : (error?.status === 403 ? 'Access denied. Admin permissions required.' : 'Failed to update investment plan. Please try again.');
      addNotification({
        title: 'Error Updating Investment Plan',
        message,
        type: 'error'
      });
      return null;
    }
  };

  // Function to delete an investment plan
  const deleteInvestmentPlan = async (planId) => {
    try {
      // Try to ensure token
      if (!localStorage.getItem('auth_token') && localStorage.getItem('refresh_token')) {
        try { await authService.refreshToken(); } catch { }
      }

      await api.planApi.deletePlan(planId);

      setInvestmentPlans(prev => prev.filter(plan => plan.id !== planId));
      // Schedule a background refresh
      setTimeout(() => { loadInvestmentPlans().catch(() => { }); }, 800);
      return true;
    } catch (error) {
      console.error('Error deleting investment plan:', error);
      const message = error?.status === 401 ? 'Authentication required or insufficient permissions.' : (error?.status === 403 ? 'Access denied. Admin permissions required.' : 'Failed to delete investment plan. Please try again.');
      addNotification({
        title: 'Error Deleting Investment Plan',
        message,
        type: 'error'
      });
      return false;
    }
  };

  // Function to get user by ID
  const getUserById = (userId) => {
    return users.find(user => user.id === userId);
  };

  // Function to get investment by ID
  const getInvestmentById = (investmentId) => {
    return userInvestments.find(inv => inv.id === investmentId);
  };

  // Function to get investments by user ID
  const getInvestmentsByUserId = (userId) => {
    return userInvestments.filter(inv => inv.userId === userId);
  };

  // Function to get investments by plan ID
  const getInvestmentsByPlanId = (planName) => {
    return userInvestments.filter(inv => inv.planName === planName);
  };

  // Load all investments (admin only) - fetches ALL investments from ALL users
  const loadAllInvestments = async () => {
    try {
      setLoadingAllInvestments(true);

      // Try to fetch from API
      try {
        const response = await api.investmentApi.getInvestments();
        // axios interceptor already returns response.data, so response is the actual data
        // Check if response is an array or has an investments property
        const rawInvestments = Array.isArray(response) ? response : (response?.investments || []);

        // Normalize investments to ensure they have proper id field and all required properties
        const investments = rawInvestments.map(normalizeInvestment);

        if (investments.length > 0 || Array.isArray(response)) {
          setAllInvestments(investments);
          localStorage.setItem('all_investments', JSON.stringify(investments));
        } else {
          // If no data from API, try localStorage
          const stored = localStorage.getItem('all_investments');
          if (stored) {
            // Also normalize stored data in case it's old format
            const storedInvestments = JSON.parse(stored);
            const normalizedStored = Array.isArray(storedInvestments)
              ? storedInvestments.map(normalizeInvestment)
              : [];
            setAllInvestments(normalizedStored);
          } else {
            setAllInvestments([]);
          }
        }
      } catch (apiError) {
        // Silently handle 404 errors (backend not available)
        if (apiError?.response?.status === 404 || apiError?.code === 'ERR_NETWORK') {
          console.log('Backend not available, using local data');
        } else {
          console.error('Error fetching investments:', apiError);
        }

        // Load from localStorage as fallback
        const stored = localStorage.getItem('all_investments');
        if (stored) {
          // Normalize stored data
          const storedInvestments = JSON.parse(stored);
          const normalizedStored = Array.isArray(storedInvestments)
            ? storedInvestments.map(normalizeInvestment)
            : [];
          setAllInvestments(normalizedStored);
        } else {
          setAllInvestments([]);
        }
      }
    } catch (error) {
      console.error('Error loading investments:', error);
      // Try localStorage as final fallback
      const stored = localStorage.getItem('all_investments');
      if (stored) {
        // Normalize stored data
        const storedInvestments = JSON.parse(stored);
        const normalizedStored = Array.isArray(storedInvestments)
          ? storedInvestments.map(normalizeInvestment)
          : [];
        setAllInvestments(normalizedStored);
      } else {
        setAllInvestments([]);
      }
    } finally {
      setLoadingAllInvestments(false);
    }
  };

  // ==================== ROI PROCESSING & SETTLEMENT FUNCTIONS ====================

  // Calculate daily ROI for an investment
  const calculateDailyROI = (investment) => {
    const { amount, status, endDate, maturityDate, planSnapshot } = investment;

    // Check if investment is active
    if (status !== 'active') return 0;

    // Check if matured (use maturityDate or endDate)
    const today = new Date().toISOString().split('T')[0];
    const maturity = maturityDate || endDate;
    if (maturity && today >= maturity) {
      console.log(`Investment ${investment.id} has matured`);
      return 0;
    }

    // Get ROI percentage - prefer planSnapshot, fallback to direct properties
    let roiPercent;
    if (planSnapshot?.roiPercentYearly) {
      // Use yearly ROI from plan snapshot (most accurate)
      roiPercent = planSnapshot.roiPercentYearly;
    } else if (planSnapshot?.roiPercentMonthly) {
      // Convert monthly to yearly
      roiPercent = planSnapshot.roiPercentMonthly * 12;
    } else if (investment.roiPercentMonthly) {
      // Fallback to investment's monthly ROI
      roiPercent = investment.roiPercentMonthly * 12;
    } else if (investment.roiRate) {
      // Legacy support
      roiPercent = investment.roiRate * 12;
    } else {
      console.warn(`No ROI percentage found for investment ${investment.id}`);
      return 0;
    }

    // Calculate daily ROI from yearly percentage
    // Formula: (Investment Amount × Yearly ROI %) ÷ 365
    const yearlyROI = (amount * roiPercent) / 100;
    const dailyROI = yearlyROI / 365;

    return Math.round(dailyROI);
  };

  // Credit ROI to user wallet and create transaction
  const creditROIToWallet = async (userId, amount, investmentId, date) => {
    try {
      // Create transaction record
      const transaction = {
        id: `txn-roi-${Date.now()}`,
        userId,
        type: 'roi_credit',
        amount,
        status: 'completed',
        investmentId,
        description: `Daily ROI Credit from Investment ${investmentId}`,
        date: date || new Date().toISOString().split('T')[0],
        createdAt: new Date().toISOString()
      };

      // Add to transactions
      setTransactions(prev => [transaction, ...prev]);

      // Update wallet balance for the user
      if (user && (user.id === userId || user._id === userId)) {
        setWalletBalance(prev => prev + amount);
      }

      // Add notification to user
      addNotification({
        title: 'ROI Credited',
        message: `₹${amount.toLocaleString()} ROI has been credited to your wallet`,
        type: 'success',
        userId
      });

      return transaction;
    } catch (error) {
      console.error('Error crediting ROI to wallet:', error);
      return null;
    }
  };

  // Helper function to get next day
  const getNextDay = (dateString) => {
    const date = new Date(dateString);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  };

  // Process daily ROI for all active investments
  const processDailyROI = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // Track processed IDs to prevent duplicates
      const processedInvestmentIds = new Set();

      // Get all active investments that haven't been processed today
      const activeInvestments = allInvestments.filter(inv => {
        if (inv.status !== 'active') return false;

        // Check if already processed today (duplicate prevention)
        const lastProcessed = inv.roiSchedule?.lastProcessedDate;
        if (lastProcessed && lastProcessed >= today) return false;

        // Check if investment has matured
        const maturity = inv.maturityDate || inv.endDate;
        if (maturity && today >= maturity) {
          // Auto-maturing investment logic would go here, for now just skip ROI
          console.log(`Auto-maturing investment ${inv.id}`);
          return false;
        }

        // Check if plan is paused (if plan info available)
        const plan = investmentPlans.find(p => p.id === inv.planId);
        if (plan?.pausedROI) {
          console.log(`Plan ${inv.planId} ROI is paused, skipping investment ${inv.id}`);
          return false;
        }

        return true;
      });

      let processedCount = 0;
      let totalROICredited = 0;
      let failedCount = 0;

      for (const investment of activeInvestments) {
        // Additional duplicate check
        if (processedInvestmentIds.has(investment.id)) {
          console.warn(`Duplicate processing attempt detected for investment ${investment.id}`);
          continue;
        }

        const dailyROI = calculateDailyROI(investment);

        if (dailyROI > 0) {
          try {
            // Credit to wallet
            const transaction = await creditROIToWallet(
              investment.userId,
              dailyROI,
              investment.id,
              today
            );

            if (transaction) {
              // Calculate maturity tracking
              const startDate = new Date(investment.startDate);
              const endDate = new Date(investment.endDate || investment.maturityDate);
              const todayDate = new Date(today);

              const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
              const elapsedDays = Math.ceil((todayDate - startDate) / (1000 * 60 * 60 * 24));
              const remainingDays = Math.max(0, totalDays - elapsedDays);
              const progressPercent = totalDays > 0 ? ((elapsedDays / totalDays) * 100).toFixed(2) : 0;

              // Update investment with new ROI schedule and maturity tracking
              const updatedInvestment = {
                ...investment,
                roiSchedule: {
                  type: 'daily',
                  lastProcessedDate: today,
                  nextProcessDate: getNextDay(today),
                  accumulatedROI: (investment.roiSchedule?.accumulatedROI || 0) + dailyROI,
                  settledROI: investment.roiSchedule?.settledROI || 0,
                  pendingSettlement: (investment.roiSchedule?.pendingSettlement || 0) + dailyROI
                },
                maturityTracking: {
                  totalDays,
                  elapsedDays,
                  remainingDays,
                  progressPercent: parseFloat(progressPercent),
                  maturityAmount: investment.amount + (investment.roiSchedule?.accumulatedROI || 0) + dailyROI
                },
                roiHistory: [
                  ...(investment.roiHistory || []),
                  {
                    id: `roi-${Date.now()}-${investment.id}`,
                    date: today,
                    amount: dailyROI,
                    type: 'daily_credit',
                    status: 'credited',
                    transactionId: transaction.id,
                    planId: investment.planId,
                    balanceAfter: walletBalance + dailyROI
                  }
                ]
              };

              // Update in both arrays
              setAllInvestments(prev => prev.map(inv =>
                inv.id === investment.id ? updatedInvestment : inv
              ));
              setUserInvestments(prev => prev.map(inv =>
                inv.id === investment.id ? updatedInvestment : inv
              ));

              processedInvestmentIds.add(investment.id);
              processedCount++;
              totalROICredited += dailyROI;
            } else {
              failedCount++;
            }
          } catch (error) {
            console.error(`Error processing ROI for investment ${investment.id}:`, error);
            failedCount++;
          }
        }
      }

      // Add admin notification
      if (processedCount > 0 || failedCount > 0) {
        addNotification({
          title: 'Daily ROI Processed',
          message: `Processed ${processedCount} investments. Total ROI credited: ₹${totalROICredited.toLocaleString()}. ${failedCount > 0 ? `Failed: ${failedCount}` : ''}`,
          type: failedCount > 0 ? 'warning' : 'success',
          audience: 'admin'
        });
      }

      return { processedCount, totalROICredited, failedCount };
    } catch (error) {
      console.error('Error processing daily ROI:', error);
      addNotification({
        title: 'ROI Processing Error',
        message: 'Failed to process daily ROI. Please try again.',
        type: 'error',
        audience: 'admin'
      });
      return { processedCount: 0, totalROICredited: 0, failedCount: 0 };
    }
  };

  // Process monthly settlement for all investments
  const processMonthlySettlement = async (month, year) => {
    try {
      const settlementMonth = `${year}-${String(month).padStart(2, '0')}`;
      const settlementDate = new Date().toISOString();

      // Get all investments with pending ROI to settle
      const investmentsToSettle = allInvestments.filter(inv =>
        inv.roiSchedule?.pendingSettlement > 0
      );

      let settledCount = 0;
      let totalSettled = 0;

      for (const investment of investmentsToSettle) {
        const settlementRecord = {
          id: `settlement-${Date.now()}-${investment.id}`,
          userId: investment.userId,
          investmentId: investment.id,
          period: settlementMonth,
          totalROI: investment.roiSchedule.pendingSettlement,
          settledDate: settlementDate,
          status: 'completed',
          action: null,
          actionDate: null,
          actionDetails: {}
        };

        // Save settlement record
        setSettlements(prev => [settlementRecord, ...prev]);

        // Update investment
        const updatedInvestment = {
          ...investment,
          roiSchedule: {
            ...investment.roiSchedule,
            settledROI: (investment.roiSchedule.settledROI || 0) + investment.roiSchedule.pendingSettlement,
            pendingSettlement: 0
          }
        };

        setAllInvestments(prev => prev.map(inv =>
          inv.id === investment.id ? updatedInvestment : inv
        ));
        setUserInvestments(prev => prev.map(inv =>
          inv.id === investment.id ? updatedInvestment : inv
        ));

        addNotification({
          title: 'ROI Settled',
          message: `₹${investment.roiSchedule.pendingSettlement.toLocaleString()} ROI has been settled. You can now withdraw or reinvest.`,
          type: 'success',
          userId: investment.userId
        });

        settledCount++;
        totalSettled += investment.roiSchedule.pendingSettlement;
      }

      setSettlementConfig(prev => ({
        ...prev,
        lastSettlementDate: settlementDate
      }));

      if (settledCount > 0) {
        addNotification({
          title: 'Settlement Processed',
          message: `Settled ${settledCount} investments. Total: ₹${totalSettled.toLocaleString()}`,
          type: 'success',
          audience: 'admin'
        });
      }

      return { settledCount, totalSettled };
    } catch (error) {
      console.error('Error processing settlement:', error);
      addNotification({
        title: 'Settlement Error',
        message: 'Failed to process settlement.',
        type: 'error',
        audience: 'admin'
      });
      return { settledCount: 0, totalSettled: 0 };
    }
  };

  // Withdraw settled ROI
  const withdrawSettledROI = async (settlementId) => {
    try {
      const settlement = settlements.find(s => s.id === settlementId);
      if (!settlement || settlement.action) {
        throw new Error('Settlement not available');
      }

      const transaction = {
        id: `txn-withdraw-${Date.now()}`,
        userId: settlement.userId,
        type: 'roi_withdrawal',
        amount: settlement.totalROI,
        status: 'pending',
        settlementId,
        description: `ROI Withdrawal for ${settlement.period}`,
        date: new Date().toISOString().split('T')[0],
        createdAt: new Date().toISOString()
      };

      setTransactions(prev => [transaction, ...prev]);
      setSettlements(prev => prev.map(s =>
        s.id === settlementId
          ? { ...s, action: 'withdrawn', actionDate: new Date().toISOString(), actionDetails: { transactionId: transaction.id } }
          : s
      ));

      addNotification({
        title: 'Withdrawal Requested',
        message: `Withdrawal for ₹${settlement.totalROI.toLocaleString()} submitted.`,
        type: 'success',
        userId: settlement.userId
      });

      return transaction;
    } catch (error) {
      console.error('Error withdrawing ROI:', error);
      throw error;
    }
  };

  // Reinvest settled ROI
  const reinvestSettledROI = async (settlementId, planId) => {
    try {
      const settlement = settlements.find(s => s.id === settlementId);
      if (!settlement || settlement.action) {
        throw new Error('Settlement not available');
      }

      const plan = investmentPlans.find(p => p.id === planId);
      if (!plan || !plan.active) {
        throw new Error('Invalid plan');
      }

      const investor = users.find(u => u.id === settlement.userId || u._id === settlement.userId);
      const startDate = new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + plan.durationMonths);

      const newInvestment = {
        id: `inv-${Date.now()}`,
        userId: settlement.userId,
        investorName: investor?.name || 'Unknown',
        investorEmail: investor?.email || '',
        planId: plan.id,
        planName: plan.title,
        amount: settlement.totalROI,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        status: 'active',
        roiPaid: 0,
        monthlyROI: Math.round((settlement.totalROI * plan.roiPercentMonthly) / 100),
        totalMonths: plan.durationMonths,
        completedMonths: 0,
        source: 'roi_reinvestment',
        sourceSettlementId: settlementId,
        roiSchedule: {
          type: 'daily',
          lastProcessedDate: null,
          accumulatedROI: 0,
          settledROI: 0,
          pendingSettlement: 0
        },
        roiHistory: []
      };

      await addInvestment(newInvestment);

      setSettlements(prev => prev.map(s =>
        s.id === settlementId
          ? { ...s, action: 'reinvested', actionDate: new Date().toISOString(), actionDetails: { newInvestmentId: newInvestment.id } }
          : s
      ));

      addNotification({
        title: 'ROI Reinvested',
        message: `₹${settlement.totalROI.toLocaleString()} reinvested in ${plan.title}`,
        type: 'success',
        userId: settlement.userId
      });

      return newInvestment;
    } catch (error) {
      console.error('Error reinvesting:', error);
      throw error;
    }
  };

  const value = {
    // Branding
    brandingSettings,
    updateBrandingSettings: updateBranding,
    // Backend status
    backendStatus,
    retryBackendCheck,

    // Investment related
    investmentPlans: Array.isArray(investmentPlans) ? investmentPlans : [],
    setInvestmentPlans,
    addInvestmentPlan,
    updateInvestmentPlan,
    deleteInvestmentPlan,
    userInvestments: Array.isArray(userInvestments) ? userInvestments : [],
    addInvestment: addInvestmentEnhanced,
    updateInvestment,
    deleteInvestment,
    processROIPayment,
    refreshInvestmentPlans,
    getUserById,
    getInvestmentById,
    getInvestmentsByUserId,
    getInvestmentsByPlanId,
    loadingInvestmentPlans,
    loadingInvestments,

    // Admin investments (all users)
    allInvestments: Array.isArray(allInvestments) ? allInvestments : [],
    loadAllInvestments,
    loadingAllInvestments,

    // Wallet and financial
    walletBalance: Number(walletBalance) || 0,
    setWalletBalance,

    // ROI Processing & Settlement
    processDailyROI,
    processMonthlySettlement,
    withdrawSettledROI,
    reinvestSettledROI,
    settlementConfig,
    setSettlementConfig,
    settlements,
    loadingSettlements,

    // ROI Schedule Settings
    roiScheduleSettings,
    setROIScheduleSettings,

    // Bank Account Management
    userBankDetails: Array.isArray(userBankDetails) ? userBankDetails : [],
    addBankAccount,
    updateBankAccount,
    deleteBankAccount,
    setPrimaryBankAccount,
    loadingBankDetails,

    // Transactions
    transactions: Array.isArray(transactions) ? transactions : [],
    addTransaction,
    getTransactionsByType,
    approveTransaction,
    rejectTransaction,
    updateTransactionStatus,
    updateTransaction,
    loadingTransactions,

    // Referral data
    referralCode: typeof referralCode === 'string' ? referralCode : '',
    referralStats: referralStats && typeof referralStats === 'object' ? referralStats : {},
    referralHistory: Array.isArray(referralHistory) ? referralHistory : [],
    loadingReferrals,

    // Wallet transactions
    walletTransactions: Array.isArray(walletTransactions) ? walletTransactions : [],
    loadingWalletTransactions,

    // Notifications
    notifications: Array.isArray(notifications) ? notifications : [],
    setNotifications,
    addNotification,
    markNotificationAsRead,
    markNotificationAsUnread,
    archiveNotification,
    unarchiveNotification,
    deleteNotification,
    markAllNotificationsAsRead,
    clearAllNotifications,
    deleteAllNotifications,
    updateNotificationPushPreference,
    loadingNotifications,

    // KYC Records (for user verification status)
    kycRecords: Array.isArray(kycRecords) ? kycRecords : [],
    setKycRecords,
    reloadKycRecords: loadKycRecords,
    loadingKycRecords,

    // Social Login Settings
    socialLoginSettings,
    setSocialLoginSettings,
    updateSocialLoginSettings,

    // Gold Rate API Settings
    goldRate,
    updateGoldRate,
    goldRateApiSettings,
    updateGoldRateApiSettings,
    fetchGoldRateFromApi,
    testProvider,
    getProviderStats,
    resetProviderErrors,
    getRateHistory,
    clearRateHistory,
    saveGoldRateSettingsToServer,
    loadGoldRateSettingsFromServer,
    loadingGoldRate,
    // Currency conversion helpers
    testCurrencyConversion,
    updateCurrencyConversionSettings,

    // Push Notifications
    pushNotificationSettings,
    pushNotificationHistory,
    updatePushNotificationSettings,
    sendPushNotification,
    getPushNotificationStats,
    togglePushNotifications,
    updateUserPushNotificationSettings,

    // User Management
    users: Array.isArray(users) ? users : [],
    setUsers,
    reloadUsers: loadUsers,
    updateUserKYCStatus,
    updateUserAccountStatus,
    updateUserInvestmentData,
    addUser,
    updateUserProfile,
    deleteUser,
    detectFraudAlerts,
    addUserFraudAlerts,
    addUserFraudAlert,
    clearUserFraudAlerts,
    removeUserFraudAlert,
    updateUserFraudAlert,
    resolveUserFraudAlert,
    resolveAllUserFraudAlerts,
    getUnresolvedUserFraudAlerts,
    getUserFraudAlertsBySeverity,
    getUserFraudAlertsByType,
    getAllFraudAlerts,
    getAllUnresolvedFraudAlerts,
    getAllFraudAlertsBySeverity,
    getAllFraudAlertsByType,
    getFraudAlertStats,
    generateFraudReport,
    exportFraudAlerts,
    importFraudAlerts,
    searchFraudAlerts,
    filterFraudAlertsByDate,
    getFraudTrendData,
    getUserRiskScore,
    getHighRiskUsers,
    addCustomFraudAlert,
    bulkResolveFraudAlerts,
    bulkDeleteFraudAlerts,
    getUserFraudAlertHistory,
    getRecentFraudAlerts,
    getFraudAlertsByUser,
    updateFraudAlertSettings,
    getFraudAlertSettings,
    resetFraudAlertSystem,
    simulateFraudDetection,
    loadingUsers
  };

  try {
    return (
      <DataContext.Provider value={value}>
        {children}
      </DataContext.Provider>
    );
  } catch (error) {
    console.error('DataProvider initialization error:', error);
    return (
      <DataContext.Provider value={{}}>
        {children}
      </DataContext.Provider>
    );
  }
};

// Load all investments (admin only) - fetches ALL investments from ALL users
const loadAllInvestments = async () => {
  setLoadingAllInvestments(true);
  try {
    const resp = await api.investmentApi.getInvestments();
    const raw = Array.isArray(resp) ? resp : (Array.isArray(resp?.investments) ? resp.investments : []);
    const investments = raw.map(normalizeInvestment);
    setAllInvestments(investments);
  } catch (error) {
    console.error('Error loading all investments:', error);
    setAllInvestments([]);
    // Only show error if it's not a 404 (missing endpoint)
    if (error?.status !== 404) {
      addNotification({
        title: 'Error Loading Investments',
        message: 'Failed to load investments data. Please try again later.',
        type: 'error',
        audience: 'system'
      });
    }
  } finally {
    setLoadingAllInvestments(false);
  }
};

// Credit ROI to user wallet and create transaction
const creditROIToWallet = async (userId, amount, investmentId, date) => {
  try {
    // Create transaction record
    const transaction = {
      id: `txn-roi-${Date.now()}`,
      userId,
      type: 'roi_credit',
      amount,
      status: 'completed',
      investmentId,
      description: `Daily ROI Credit from Investment ${investmentId}`,
      date: date || new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString()
    };

    // Add to transactions
    setTransactions(prev => [transaction, ...prev]);

    // Update wallet balance for the user
    if (user && (user.id === userId || user._id === userId)) {
      setWalletBalance(prev => prev + amount);
    }

    // Add notification to user
    addNotification({
      title: 'ROI Credited',
      message: `₹${amount.toLocaleString()} ROI has been credited to your wallet`,
      type: 'success',
      userId
    });

    return transaction;
  } catch (error) {
    console.error('Error crediting ROI to wallet:', error);
    return null;
  }
};

// Helper function to get next day
const getNextDay = (dateString) => {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

// Process daily ROI for all active investments
const processDailyROI = async () => {
  try {
    const today = new Date().toISOString().split('T')[0];

    // Get all active investments that haven't been processed today
    const activeInvestments = allInvestments.filter(inv => {
      if (inv.status !== 'active') return false;

      // Check if already processed today
      const lastProcessed = inv.roiSchedule?.lastProcessedDate;
      if (lastProcessed && lastProcessed >= today) return false;

      return true;
    });

    let processedCount = 0;
    let totalROICredited = 0;

    for (const investment of activeInvestments) {
      const dailyROI = calculateDailyROI(investment);

      if (dailyROI > 0) {
        // Credit to wallet
        const transaction = await creditROIToWallet(
          investment.userId,
          dailyROI,
          investment.id,
          today
        );

        if (transaction) {
          // Update investment with new ROI schedule
          const updatedInvestment = {
            ...investment,
            roiSchedule: {
              type: 'daily',
              lastProcessedDate: today,
              nextProcessDate: getNextDay(today),
              accumulatedROI: (investment.roiSchedule?.accumulatedROI || 0) + dailyROI,
              settledROI: investment.roiSchedule?.settledROI || 0,
              pendingSettlement: (investment.roiSchedule?.pendingSettlement || 0) + dailyROI
            },
            roiHistory: [
              ...(investment.roiHistory || []),
              {
                id: `roi-${Date.now()}-${investment.id}`,
                date: today,
                amount: dailyROI,
                type: 'daily_credit',
                status: 'credited',
                transactionId: transaction.id
              }
            ]
          };

          // Update in both arrays
          setAllInvestments(prev => prev.map(inv =>
            inv.id === investment.id ? updatedInvestment : inv
          ));
          setUserInvestments(prev => prev.map(inv =>
            inv.id === investment.id ? updatedInvestment : inv
          ));

          processedCount++;
          totalROICredited += dailyROI;
        }
      }
    }

    // Add admin notification
    if (processedCount > 0) {
      addNotification({
        title: 'Daily ROI Processed',
        message: `Processed ${processedCount} investments. Total ROI credited: ₹${totalROICredited.toLocaleString()}`,
        type: 'success',
        audience: 'admin'
      });
    }

    return { processedCount, totalROICredited };
  } catch (error) {
    console.error('Error processing daily ROI:', error);
    addNotification({
      title: 'ROI Processing Error',
      message: 'Failed to process daily ROI. Please try again.',
      type: 'error',
      audience: 'admin'
    });
    return { processedCount: 0, totalROICredited: 0 };
  }
};

// Process monthly settlement for all investments
const processMonthlySettlement = async (month, year) => {
  try {
    const settlementMonth = `${year}-${String(month).padStart(2, '0')}`;
    const settlementDate = new Date().toISOString();

    // Get all investments with pending ROI to settle
    const investmentsToSettle = allInvestments.filter(inv =>
      inv.roiSchedule?.pendingSettlement > 0
    );

    let settledCount = 0;
    let totalSettled = 0;

    for (const investment of investmentsToSettle) {
      const settlementRecord = {
        id: `settlement-${Date.now()}-${investment.id}`,
        userId: investment.userId,
        investmentId: investment.id,
        period: settlementMonth,
        totalROI: investment.roiSchedule.pendingSettlement,
        settledDate: settlementDate,
        status: 'completed',
        action: null,
        actionDate: null,
        actionDetails: {}
      };

      // Save settlement record
      setSettlements(prev => [settlementRecord, ...prev]);

      // Update investment
      const updatedInvestment = {
        ...investment,
        roiSchedule: {
          ...investment.roiSchedule,
          settledROI: (investment.roiSchedule.settledROI || 0) + investment.roiSchedule.pendingSettlement,
          pendingSettlement: 0
        }
      };

      setAllInvestments(prev => prev.map(inv =>
        inv.id === investment.id ? updatedInvestment : inv
      ));
      setUserInvestments(prev => prev.map(inv =>
        inv.id === investment.id ? updatedInvestment : inv
      ));

      addNotification({
        title: 'ROI Settled',
        message: `₹${investment.roiSchedule.pendingSettlement.toLocaleString()} ROI has been settled. You can now withdraw or reinvest.`,
        type: 'success',
        userId: investment.userId
      });

      settledCount++;
      totalSettled += investment.roiSchedule.pendingSettlement;
    }

    setSettlementConfig(prev => ({
      ...prev,
      lastSettlementDate: settlementDate
    }));

    if (settledCount > 0) {
      addNotification({
        title: 'Settlement Processed',
        message: `Settled ${settledCount} investments. Total: ₹${totalSettled.toLocaleString()}`,
        type: 'success',
        audience: 'admin'
      });
    }

    return { settledCount, totalSettled };
  } catch (error) {
    console.error('Error processing settlement:', error);
    addNotification({
      title: 'Settlement Error',
      message: 'Failed to process settlement.',
      type: 'error',
      audience: 'admin'
    });
    return { settledCount: 0, totalSettled: 0 };
  }
};

// Withdraw settled ROI
const withdrawSettledROI = async (settlementId) => {
  try {
    const settlement = settlements.find(s => s.id === settlementId);
    if (!settlement || settlement.action) {
      throw new Error('Settlement not available');
    }

    const transaction = {
      id: `txn-withdraw-${Date.now()}`,
      userId: settlement.userId,
      type: 'roi_withdrawal',
      amount: settlement.totalROI,
      status: 'pending',
      settlementId,
      description: `ROI Withdrawal for ${settlement.period}`,
      date: new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString()
    };

    setTransactions(prev => [transaction, ...prev]);
    setSettlements(prev => prev.map(s =>
      s.id === settlementId
        ? { ...s, action: 'withdrawn', actionDate: new Date().toISOString(), actionDetails: { transactionId: transaction.id } }
        : s
    ));

    addNotification({
      title: 'Withdrawal Requested',
      message: `Withdrawal for ₹${settlement.totalROI.toLocaleString()} submitted.`,
      type: 'success',
      userId: settlement.userId
    });

    return transaction;
  } catch (error) {
    console.error('Error withdrawing ROI:', error);
    throw error;
  }
};

// Reinvest settled ROI
const reinvestSettledROI = async (settlementId, planId) => {
  try {
    const settlement = settlements.find(s => s.id === settlementId);
    if (!settlement || settlement.action) {
      throw new Error('Settlement not available');
    }

    const plan = investmentPlans.find(p => p.id === planId);
    if (!plan || !plan.active) {
      throw new Error('Invalid plan');
    }

    const investor = users.find(u => u.id === settlement.userId || u._id === settlement.userId);
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + plan.durationMonths);

    const newInvestment = {
      id: `inv-${Date.now()}`,
      userId: settlement.userId,
      investorName: investor?.name || 'Unknown',
      investorEmail: investor?.email || '',
      planId: plan.id,
      planName: plan.title,
      amount: settlement.totalROI,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      status: 'active',
      roiPaid: 0,
      monthlyROI: Math.round((settlement.totalROI * plan.roiPercentMonthly) / 100),
      totalMonths: plan.durationMonths,
      completedMonths: 0,
      source: 'roi_reinvestment',
      sourceSettlementId: settlementId,
      roiSchedule: {
        type: 'daily',
        lastProcessedDate: null,
        accumulatedROI: 0,
        settledROI: 0,
        pendingSettlement: 0
      },
      roiHistory: []
    };

    await addInvestment(newInvestment);

    setSettlements(prev => prev.map(s =>
      s.id === settlementId
        ? { ...s, action: 'reinvested', actionDate: new Date().toISOString(), actionDetails: { newInvestmentId: newInvestment.id } }
        : s
    ));

    addNotification({
      title: 'ROI Reinvested',
      message: `₹${settlement.totalROI.toLocaleString()} reinvested in ${plan.title}`,
      type: 'success',
      userId: settlement.userId
    });

    return newInvestment;
  } catch (error) {
    console.error('Error reinvesting:', error);
    throw error;
  }
};

export default DataProvider;
